import jsPDF from 'jspdf';
import { formatDate, sv, buildTruckReceiptData, drawHangingParagraph, drawSpacedTitle, LW, M, FONT, MM_PER_PT, layoutGeo, layoutFieldHidden } from './helpers';
import { resolvePdfData } from './pdfEditSchemas';

// ═══════════════════════════════════════════════════════════════════════════════
// CERTIFICATE OF ORIGIN PDF
// ═══════════════════════════════════════════════════════════════════════════════

export function generateCertificateOfOriginPDF(document, company, authority, returnBlob = false) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2;

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, 'certificate');

  let y = M + 8;
  const LH = 4; // line height for 9pt text
  const colRight = M + cw * 0.38; // right column start for two-column sections
  const compName = ed.exporter_name;

  // ── Helper: render bold name followed by normal continuation text ────
  const renderNameBlock = (name, restText, maxW) => {
    doc.setFontSize(9);
    doc.setFont(FONT, 'normal');
    const full = `${name} ${restText}`;
    const lines = doc.splitTextToSize(full, maxW);
    for (let i = 0; i < lines.length; i++) {
      if (i === 0 && lines[0].startsWith(name)) {
        doc.setFont(FONT, 'bold');
        doc.text(name, M, y);
        const nw = doc.getTextWidth(name);
        doc.setFont(FONT, 'normal');
        const r = lines[0].substring(name.length);
        if (r) doc.text(r, M + nw, y);
      } else {
        doc.setFont(FONT, 'normal');
        doc.text(lines[i], M, y);
      }
      y += LH;
    }
  };

  // ── Company Block (bold name + address + email + GSTIN) ─────────────
  renderNameBlock(compName, ed.exporter_details, cw * 0.55);
  y += 3;

  // ── Buyer Block (bold name + address) ───────────────────────────────
  renderNameBlock(ed.buyer_name, ed.buyer_details, cw * 0.55);
  y += 20;

  // ── Transport Mode | Place of Origin ────────────────────────────────
  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');
  doc.text(ed.transport_mode, M, y);
  doc.text(ed.place_of_origin, colRight, y);
  y += 14;

  // ── Port of Discharge | Final Destination ───────────────────────────
  doc.text(ed.port_of_discharge, M, y);
  doc.text(ed.final_destination, colRight, y);
  y += 20;

  // ── Gross Weight (right aligned) ────────────────────────────────────
  doc.text(ed.gross_weight, pw - M, y, { align: 'right' });
  y += 10;

  // ── Packing Type | Goods Description ────────────────────────────────
  doc.text(ed.packing_type, M + cw * 0.22, y);
  doc.setTextColor(180, 0, 0);
  doc.text(ed.goods_desc, M + cw * 0.32, y);
  doc.setTextColor(0, 0, 0);
  y += 16;

  // ── Net Weight (right aligned) ──────────────────────────────────────
  doc.text(ed.net_weight, pw - M, y, { align: 'right' });
  y += 22;

  // ── Certification + USD Value ───────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');
  doc.text(ed.cert_line, M, y);
  doc.text(ed.usd_value, pw - M, y, { align: 'right' });
  y += 5;

  // ── TIN / VAT/BIN / IRC / BIN / HS CODE ────────────────────────────
  const tinLines = doc.splitTextToSize(ed.tin_line, cw * 0.7);
  doc.text(tinLines, M, y);
  y += tinLines.length * LH + 4;

  // ── Notify ──────────────────────────────────────────────────────────
  const notifyTxt = ed.notify;

  doc.setFont(FONT, 'bold');
  const nLabel = 'Notify  :  ';
  doc.text(nLabel, M, y);
  const nLabelW = doc.getTextWidth(nLabel);
  doc.setFont(FONT, 'normal');

  const nFirstSplit = doc.splitTextToSize(notifyTxt, cw - nLabelW);
  if (nFirstSplit.length > 0) {
    doc.text(nFirstSplit[0], M + nLabelW, y);
    const consumed = nFirstSplit[0].length;
    const rest = notifyTxt.substring(consumed).trim();
    if (rest) {
      const restLines = doc.splitTextToSize(rest, cw);
      for (const l of restLines) {
        y += LH;
        doc.text(l, M, y);
      }
    }
  }
  y += 20;

  // ── Signature: For, COMPANY NAME (right aligned) ────────────────────
  doc.setFont(FONT, 'normal');
  const forStr = 'For, ';
  const forW = doc.getTextWidth(forStr);
  doc.setFont(FONT, 'bold');
  const cnW = doc.getTextWidth(compName);
  const sigStartX = pw - M - forW - cnW;
  doc.setFont(FONT, 'normal');
  doc.text(forStr, sigStartX, y);
  doc.setFont(FONT, 'bold');
  doc.text(compName, sigStartX + forW, y);
  y += 15;

  // ── Registration Details + Director ─────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  const rc1 = M + cw * 0.15; // left column center
  const rc2 = M + cw * 0.48; // right column center

  // Row 1: SB No / SB Date
  doc.text(ed.sb_no, rc1, y, { align: 'center' });
  doc.text(ed.sb_date, rc2, y, { align: 'center' });
  // Director (right aligned, same row)
  doc.text(ed.signatory, pw - M, y, { align: 'right' });
  y += 8;

  // Row 2: LC No / LC Date
  doc.text(ed.lc_no, rc1, y, { align: 'center' });
  doc.text(ed.lc_date, rc2, y, { align: 'center' });
  y += 10;

  // Row 3: Invoice No / Invoice Date
  doc.text(ed.invoice_no, rc1, y, { align: 'center' });
  doc.text(ed.invoice_date, rc2, y, { align: 'center' });
  y += 5;

  // Row 4: Proforma Invoice reference
  if (ed.proforma_line) {
    doc.text(ed.proforma_line, rc1 - cw * 0.05, y);
    y += 8;
  } else {
    y += 4;
  }

  // Row 5: IEC
  doc.text(ed.iec_line, rc1, y, { align: 'center' });

  // ── Page footer ─────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);

    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);

    doc.text(footerDate, M, ph - 5);
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });

    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`CertificateOfOrigin_${sv(document.invoice_no, document.id)}.pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// BILL OF EXCHANGE PDF (First / Second) — Official Format
// ═══════════════════════════════════════════════════════════════════════════════

function renderBillOfExchangePDF(document, company, authority, docType, ordinalLabel, returnBlob) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2;

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  let y = M;

  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, docType);
  const companyNameUpper = sv(company.exporter_name, '').toUpperCase();

  // ── Company Header (large bold) ─────────────────────────────────────
  doc.setFontSize(16);
  doc.setFont(FONT, 'bold');
  doc.text(companyNameUpper, M, y + 6);
  y += 11;

  // ── Address + Email block (narrow column, wrapping text) ────────────
  doc.setFontSize(8);
  doc.setFont(FONT, 'normal');
  let addrText = sv(company.exporter_address);
  if (company.email) {
    addrText += ` E MAIL:${sv(company.email)},`;
  }
  const addrColW = cw * 0.42;
  const addrLines = doc.splitTextToSize(addrText, addrColW);
  doc.text(addrLines, M, y);
  y += addrLines.length * 3.5;

  if (company.gstin) {
    doc.text(`GSTIN: ${sv(company.gstin)}`, M, y);
    y += 3.5;
  }
  if (company.iec) {
    doc.text(`IEC NO: ${sv(company.iec)}`, M, y);
    y += 3.5;
  }
  y += 2;

  // ── Colored Separator Line (Saffron + Green) ──────────────────────────
  doc.setLineWidth(0.7);
  doc.setDrawColor(255, 153, 51); // Saffron
  doc.line(M, y, pw - M, y);
  y += 1.2;
  doc.setDrawColor(19, 136, 8); // Green
  doc.line(M, y, pw - M, y);
  doc.setDrawColor(0);
  doc.setLineWidth(LW);
  y += 5;

  // ── Date (left side) ──────────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');
  doc.text(ed.date_line, M, y);
  y += 10;

  // ── Title (bold, centered, underlined, spaced letters) ────────────────
  doc.setFontSize(14);
  doc.setFont(FONT, 'bold');
  const titleText = 'B I L L   O F   E X C H A N G E   /   D R A F T';
  doc.text(titleText, pw / 2, y, { align: 'center' });
  const titleW = doc.getTextWidth(titleText);
  const titleX = (pw - titleW) / 2;
  doc.setLineWidth(0.5);
  doc.line(titleX, y + 1.5, titleX + titleW, y + 1.5);
  doc.setLineWidth(LW);
  y += 10;

  // ── Bank and L/C Information ──────────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');

  const drawnOnLines = doc.splitTextToSize(ed.drawn_on, cw);
  doc.text(drawnOnLines, M, y);
  y += drawnOnLines.length * 4 + 1;

  doc.text(ed.dated_line, M, y);
  y += 7;

  // ── REF NOS (underlined) ──────────────────────────────────────────────
  doc.text(ed.ref_line, M, y);
  const refW = doc.getTextWidth(ed.ref_line);
  doc.setLineWidth(0.3);
  doc.line(M, y + 1, M + refW, y + 1);
  y += 7;

  // ── EXCHANGE FOR (underlined) ─────────────────────────────────────────
  doc.text(ed.exchange_line, M, y);
  const exchW = doc.getTextWidth(ed.exchange_line);
  doc.line(M, y + 1, M + exchW, y + 1);
  doc.setLineWidth(LW);
  y += 12;

  // ── Main Declaration Paragraph ────────────────────────────────────────
  const mainLines = doc.splitTextToSize(ed.main_text, cw);
  // Flush both edges; jsPDF leaves the last line ragged, as justification should
  doc.text(mainLines, M, y, { align: 'justify', maxWidth: cw });
  y += mainLines.length * 4 + 20;

  // ── TO Section (LC Opening Bank — bold red label) ─────────────────────
  doc.setFont(FONT, 'bold');
  doc.setTextColor(180, 0, 0);
  doc.text('TO,', M, y);
  doc.setTextColor(0, 0, 0);
  y += 5;

  doc.setFont(FONT, 'normal');
  const toBankLines = doc.splitTextToSize(ed.to_block, cw * 0.45);
  doc.text(toBankLines, M, y);
  y += toBankLines.length * 4 + 12;

  // ── A/C OF Section (Buyer — bold red label) ───────────────────────────
  doc.setFont(FONT, 'bold');
  doc.setTextColor(180, 0, 0);
  doc.text('A/C OF,', M, y);
  doc.setTextColor(0, 0, 0);
  y += 5;

  doc.setFont(FONT, 'normal');
  const buyerLines = doc.splitTextToSize(ed.ac_of_block, cw * 0.45);
  doc.text(buyerLines, M, y);
  y += buyerLines.length * 4 + 25;

  // ── Signature Section ("For," normal + company name bold, right-aligned) ──
  doc.setFontSize(9);
  doc.setFont(FONT, 'bold');
  const compNameW = doc.getTextWidth(companyNameUpper);
  doc.setFont(FONT, 'normal');
  const forPrefix = 'For, ';
  const forPrefixW = doc.getTextWidth(forPrefix);
  const sigRightEdge = pw - M;
  const sigStartX = sigRightEdge - compNameW - forPrefixW;

  doc.text(forPrefix, sigStartX, y);
  doc.setFont(FONT, 'bold');
  doc.text(companyNameUpper, sigStartX + forPrefixW, y);
  y += 20;

  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  doc.text(ed.signatory, pw - M, y, { align: 'right' });

  // ── Page footer ───────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);

    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);

    doc.text(footerDate, M, ph - 5);
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });

    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`BillOfExchange_${ordinalLabel}_${sv(document.invoice_no, document.id)}.pdf`);
}

export function generateBillOfExchangeFirstPDF(document, company, authority, returnBlob = false) {
  return renderBillOfExchangePDF(document, company, authority, 'billFirst', 'First', returnBlob);
}

export function generateBillOfExchangeSecondPDF(document, company, authority, returnBlob = false) {
  return renderBillOfExchangePDF(document, company, authority, 'billSecond', 'Second', returnBlob);
}

// ── RE : reference block ────────────────────────────────────────────────────
// The reference block shared by the letters issued "TO WHOMSOEVER IT MAY
// CONCERN", typed as one hanging paragraph: only the opening line clears the
// bold "RE : " label, the rest hangs at the block's left edge, every line but
// the last is flush to both margins, and a rule closes the block off from the
// certification below it. Returns the y the next section starts at.
function drawReBlock(doc, reContent, { x, rightX, y }) {
  doc.setFontSize(9);
  doc.setFont(FONT, 'bold');
  const label = 'RE : ';
  doc.text(label, x, y);
  const labelWidth = doc.getTextWidth(label);

  doc.setFont(FONT, 'normal');
  // An invoice with nothing to reference still gets its label row, so the
  // rule below lands under the label instead of struck through it.
  const height = drawHangingParagraph(doc, reContent, {
    x, firstX: x + labelWidth, rightX, y,
  }) || 4;

  // The rule sits just under the last line, the way the letter is typed.
  const ruleY = y + height - 2;
  doc.setLineWidth(LW);
  doc.line(x, ruleY, rightX, ruleY);
  return ruleY + 12;
}

// ═══════════════════════════════════════════════════════════════════════════════
// NON-NEGOTIABLE DOCUMENTS PDF - Official Format
// ═══════════════════════════════════════════════════════════════════════════════

export function generateNonNegotiableDocsPDF(document, company, authority, returnBlob = false) {
  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, 'nonNegotiable');
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2;

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  let y = M + 5;

  // ── Company Header ────────────────────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont(FONT, 'bold');
  const companyNameUpper = sv(company.exporter_name, '').toUpperCase();
  doc.text(companyNameUpper, M, y);
  y += 6;

  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');
  const headerAddr = sv(company.exporter_address) +
    (company.email ? ` E MAIL:${sv(company.email)},` : '');
  const companyLines = doc.splitTextToSize(headerAddr, cw * 0.6);
  doc.text(companyLines, M, y);
  y += companyLines.length * 4;

  if (company.gstin) {
    doc.text(`GSTIN: ${sv(company.gstin)}`, M, y);
    y += 4;
  }
  doc.text(`IEC NO: ${sv(company.iec)}`, M, y);
  y += 8;

  // ── Black horizontal line ─────────────────────────────────────────────
  doc.setLineWidth(0.5);
  doc.line(M, y, pw - M, y);
  y += 8;

  // ── Date (right aligned) ──────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');
  doc.text(ed.date_line, pw - M, y, { align: 'right' });
  y += 12;

  // ── Title: TO WHOMSOEVER IT MAY CONCERN ───────────────────────────────
  doc.setFontSize(12);
  doc.setFont(FONT, 'bold');
  const titleText = 'TO WHOMSOEVER IT MAY CONCERN';
  doc.text(titleText, pw / 2, y, { align: 'center' });

  // Underline the title
  const titleWidth = doc.getTextWidth(titleText);
  doc.line(pw / 2 - titleWidth / 2, y + 1, pw / 2 + titleWidth / 2, y + 1);
  y += 14;

  // ── RE Section (hanging paragraph, underlined) ──────────────────────────────
  y = drawReBlock(doc, ed.re_content, { x: M + 4, rightX: pw - M, y });

  // ── Certification Statement ───────────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');

  const certText = ed.cert_text;

  const certLines = doc.splitTextToSize(certText, cw);
  doc.text(certLines, M, y, { align: 'justify', maxWidth: cw });
  y += certLines.length * 4 + 8;

  // ── Bullet List ───────────────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');

  // Bullet 1: Commercial Invoice
  const bullet1 = ed.bullet1;
  const bullet1Lines = doc.splitTextToSize(bullet1, cw - 6);
  doc.circle(M + 1, y - 1, 0.8, 'F');
  doc.text(bullet1Lines, M + 4, y);
  y += bullet1Lines.length * 4 + 2;

  // Bullet 2: Details Packing List
  doc.circle(M + 1, y - 1, 0.8, 'F');
  doc.text(ed.bullet2, M + 4, y);
  y += 6;

  // Bullet 3: Dispatch Document
  const bullet3 = ed.bullet3;
  const bullet3Lines = doc.splitTextToSize(bullet3, cw - 6);
  doc.circle(M + 1, y - 1, 0.8, 'F');
  doc.text(bullet3Lines, M + 4, y);
  y += bullet3Lines.length * 4 + 25;

  // ── Signature Section ─────────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');
  const forLabel = 'FOR, ';
  doc.text(forLabel, M, y);
  const forLabelWidth = doc.getTextWidth(forLabel);
  doc.setFont(FONT, 'bold');
  doc.text(companyNameUpper, M + forLabelWidth, y);
  y += 18;

  doc.setFont(FONT, 'italic');
  doc.setFontSize(9);
  doc.text('AUTHORISED SIGNATORY.', M, y);

  // ── Page footer ───────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);

    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);

    // Left: Date
    doc.text(footerDate, M, ph - 5);

    // Center: Page number
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });

    // Right: Company name
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });

    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`NonNegotiableDocs_${sv(document.invoice_no, document.id)}.pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// FORM SDF PDF - Official Format (APPENDIX-I)
// ═══════════════════════════════════════════════════════════════════════════════

export function generateFormSDFPDF(document, company, authority, returnBlob = false) {
  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, 'formSDF');
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2;

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  let y = M + 5;

  // ── APPENDIX-I (centered, underlined) ─────────────────────────────────
  doc.setFontSize(11);
  doc.setFont(FONT, 'bold');
  const appText = 'APPENDIX-I';
  doc.text(appText, pw / 2, y, { align: 'center' });
  const appW = doc.getTextWidth(appText);
  doc.line((pw - appW) / 2, y + 1, (pw + appW) / 2, y + 1);
  y += 10;

  // ── FORM SDF (large, bold, spaced) ────────────────────────────────────
  doc.setFontSize(20);
  doc.setFont(FONT, 'bold');
  doc.text('F O R M   S D F', pw / 2, y, { align: 'center' });
  y += 5;

  doc.setFontSize(8);
  doc.setFont(FONT, 'normal');
  doc.text('[See Regulation 3(1)]', pw / 2, y, { align: 'center' });
  y += 3.5;
  doc.text('(In Duplicate)', pw / 2, y, { align: 'center' });
  y += 12;

  // ── Shipping Bill No. and Date (with underline fields) ────────────────
  doc.setFontSize(10);
  doc.setFont(FONT, 'normal');
  const sbNo = ed.sb_no;
  const sbDate = ed.sb_date;

  doc.text('Shipping Bill No.', M, y);
  const sbFX = M + doc.getTextWidth('Shipping Bill No. ');
  doc.line(sbFX, y + 1, sbFX + 55, y + 1);
  if (sbNo) doc.text(sbNo, sbFX + 1, y);

  doc.text('Date', pw - M - 30, y);
  const dtFX = pw - M - 30 + doc.getTextWidth('Date ');
  doc.line(dtFX, y + 1, pw - M, y + 1);
  if (sbDate) doc.text(sbDate, dtFX + 1, y);
  y += 12;

  // ── Declaration Title ─────────────────────────────────────────────────
  doc.setFontSize(11);
  doc.setFont(FONT, 'bold');
  doc.text('Declaration under Foreign Exchange Management Act 1999', M, y);
  y += 10;

  // ── Declaration Bullets (justified text) ──────────────────────────────
  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');

  const bi = 8;              // bullet text indent from margin
  const bx = M + bi;         // bullet text X position
  const bw = cw - bi;        // bullet text width
  const si = 16;             // sub-bullet text indent from margin
  const sx = M + si;         // sub-bullet text X position
  const sw = cw - si;        // sub-bullet text width
  // Compute exact line height matching jsPDF internal rendering
  const lh = (doc.getFontSize() * doc.getLineHeightFactor()) / (72 / 25.4);

  // Helper: underline specific text regions within justified text blocks
  const underlineInJustified = (lines, fullText, regions, startY, textX, textW) => {
    let cumPos = 0;
    const lineRanges = [];
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      const pos = fullText.indexOf(trimmed, cumPos);
      if (pos >= 0) {
        lineRanges.push({ start: pos, end: pos + trimmed.length, text: trimmed });
        cumPos = pos + trimmed.length;
      }
    }
    for (let i = 0; i < lineRanges.length; i++) {
      const lr = lineRanges[i];
      const lineY = startY + i * lh;
      const isLast = i === lineRanges.length - 1;
      const natW = doc.getTextWidth(lr.text);
      const gaps = (lr.text.match(/ /g) || []).length;
      const extra = (!isLast && gaps > 0 && natW < textW * 0.98) ? (textW - natW) / gaps : 0;

      for (const region of regions) {
        const os = Math.max(region.start, lr.start);
        const oe = Math.min(region.end, lr.end);
        if (os >= oe) continue;

        const ls = os - lr.start;
        const le = oe - lr.start;
        const before = lr.text.substring(0, ls);
        const target = lr.text.substring(ls, le);
        const bNatW = doc.getTextWidth(before);
        const bGaps = (before.match(/ /g) || []).length;
        const tNatW = doc.getTextWidth(target);
        const tGaps = (target.match(/ /g) || []).length;
        const x1 = textX + bNatW + bGaps * extra;
        const x2 = x1 + tNatW + tGaps * extra;
        doc.line(x1, lineY + 1, x2, lineY + 1);
      }
    }
  };

  // ── Bullet 1: Seller declaration ──────────────────────────────────────
  const sbNoD = sbNo || '______________________________';
  const sbDateD = sbDate || '________________';
  const b1Text = `I/We hereby declare that I/We am/are the seller/ consignor of the goods in respect of which this declaration is made and that the particulars given in the Shipping Bill No. ${sbNoD} dated ${sbDateD} are true that`;

  doc.text('\u2022', M + 2, y);
  const b1Lines = doc.splitTextToSize(b1Text, bw);
  doc.text(b1Text, bx, y, { maxWidth: bw, align: 'justify' });
  y += b1Lines.length * lh + 2;

  // Sub-bullet 1 (open circle ○)
  const s1Text = 'The value as contracted with the buyer is same as the full export value declared in the above Shipping Bill.';
  doc.circle(M + bi + 3, y - 1, 1, 'S');
  const s1Lines = doc.splitTextToSize(s1Text, sw);
  doc.text(s1Text, sx, y, { maxWidth: sw, align: 'justify' });
  y += s1Lines.length * lh + 2;

  // Sub-bullet 2 (open circle ○)
  const s2Text = 'The full export value of the goods is not ascertainable at the time of export and that the value declared is that which I/We having regard to prevailing market conditions, expect to receive on the sale of goods in the overseas market.';
  doc.circle(M + bi + 3, y - 1, 1, 'S');
  const s2Lines = doc.splitTextToSize(s2Text, sw);
  doc.text(s2Text, sx, y, { maxWidth: sw, align: 'justify' });
  y += s2Lines.length * lh + 4;

  // ── Bullet 2: Bank undertaking (with underlined bank name & SIX MONTHS.) ──
  const bankName = ed.bank_name;
  const bankDisplay = bankName || '_________________';
  const b2Text = `I/We undertake that I/We will deliver to the Bank named herein ${bankDisplay} the foreign exchange representing the full export value of goods before SIX MONTHS.    In the manner prescribed in Rule 9 of the Foreign Exchange Management Act 1999.`;

  doc.text('\u2022', M + 2, y);
  const b2Lines = doc.splitTextToSize(b2Text, bw);
  const b2StartY = y;
  doc.text(b2Text, bx, y, { maxWidth: bw, align: 'justify' });

  // Underline bank name and SIX MONTHS. within the justified text
  const underlineRegions = [];
  if (bankName) {
    const bkIdx = b2Text.indexOf(bankDisplay);
    underlineRegions.push({ start: bkIdx, end: bkIdx + bankDisplay.length });
  }
  const sixIdx = b2Text.indexOf('SIX MONTHS.');
  underlineRegions.push({ start: sixIdx, end: sixIdx + 'SIX MONTHS.'.length });
  underlineInJustified(b2Lines, b2Text, underlineRegions, b2StartY, bx, bw);

  y += b2Lines.length * lh + 4;

  // ── Bullet 3: Residency ───────────────────────────────────────────────
  doc.text('\u2022', M + 2, y);
  doc.text('I/We further declare that I/We am/are resident and I/We have place of business in India.', bx, y);
  y += lh + 3;

  // ── Bullet 4: Caution list ────────────────────────────────────────────
  doc.text('\u2022', M + 2, y);
  doc.text('I/We am/are not in the caution list of Reserve Bank of India.', bx, y);
  y += lh + 12;

  // ── For, COMPANY NAME (right-aligned) ─────────────────────────────────
  doc.setFont(FONT, 'bold');
  doc.setFontSize(10);
  const compName = ed.exporter_name;
  doc.text(`For, ${compName}`, pw - M - 2, y, { align: 'right' });
  y += 16;

  // ── Date and Signature ────────────────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(10);
  doc.text('Date', M, y);
  doc.line(M + doc.getTextWidth('Date '), y + 1, M + 40, y + 1);
  doc.text('(Signature of the Exporter)', pw - M - 2, y, { align: 'right' });
  y += 14;

  // ── Name of the Exporter (underlined value) ───────────────────────────
  doc.setFont(FONT, 'bold');
  doc.setFontSize(10);
  const valueX = M + 50;
  const valueW = pw - M - valueX;

  doc.text('Name of the Exporter', M, y);
  doc.setFont(FONT, 'normal');
  const nameVal = `: ${compName}`;
  doc.text(nameVal, valueX - 4, y);
  const nameValW = doc.getTextWidth(nameVal);
  doc.line(valueX - 4, y + 1, valueX - 4 + nameValW, y + 1);
  y += 10;

  // ── Address (underlined multi-line values) ────────────────────────────
  doc.setFont(FONT, 'bold');
  doc.setFontSize(10);
  doc.text('Address', M, y);
  doc.setFont(FONT, 'normal');

  const addrFull = ed.exporter_address;

  if (addrFull) {
    const addrLines = doc.splitTextToSize(addrFull, valueW);
    for (let i = 0; i < addrLines.length; i++) {
      const ly = y + i * 5;
      doc.text(addrLines[i], valueX, ly);
      const lineW = doc.getTextWidth(addrLines[i]);
      doc.line(valueX, ly + 1, valueX + lineW, ly + 1);
    }
    y += addrLines.length * 5;
  }

  // ── Footer Notes (positioned near bottom) ─────────────────────────────
  y = Math.max(y + 20, ph - 55);
  doc.setFontSize(7.5);
  doc.setFont(FONT, 'normal');

  doc.text('\u2022', M + 2, y);
  const n1 = 'State appropriate date of delivery which must be date due for payment or within six months from the date of shipment, whichever is earlier, but export to warehouse established outside India with permission of Reserve Bank for delivery must within fifteen months.';
  const n1Lines = doc.splitTextToSize(n1, cw - 8);
  doc.text(n1Lines, M + 6, y);
  y += n1Lines.length * 3.2 + 3;

  doc.text('\u2022', M + 2, y);
  doc.text('Strike out whichever is not applicable.', M + 6, y);

  // ── Page footer ───────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);

    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);

    doc.text(footerDate, M, ph - 5);
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });

    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`FormSDF_${sv(document.invoice_no, document.id)}.pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// REGISTERED TRUCK OPERATORS PDF
// ═══════════════════════════════════════════════════════════════════════════════

export function generateTruckOperatorsPDF(document, company, authority, returnBlob = false) {
  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, 'truckOperators');
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2;

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  // Start lower to leave upper space for letterhead / stamp paper
  let y = 45;

  // ── Company Header ────────────────────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont(FONT, 'bold');
  const companyNameUpper = sv(company.exporter_name, '').toUpperCase();
  doc.text(companyNameUpper, M, y);
  y += 6;

  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');
  const headerAddr = sv(company.exporter_address) +
    (company.email ? ` E MAIL:${sv(company.email)},` : '');
  const companyLines = doc.splitTextToSize(headerAddr, cw * 0.6);
  doc.text(companyLines, M, y);
  y += companyLines.length * 4;

  if (company.gstin) {
    doc.text(`GSTIN: ${sv(company.gstin)}`, M, y);
    y += 4;
  }
  doc.text(`IEC NO: ${sv(company.iec)}`, M, y);
  y += 8;

  // ── Black horizontal line ─────────────────────────────────────────────
  doc.setLineWidth(0.5);
  doc.line(M, y, pw - M, y);
  y += 8;

  // ── Date (right aligned) ──────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');
  doc.text(ed.date_line, pw - M, y, { align: 'right' });
  y += 12;

  // ── Title: TO WHOMSOEVER IT MAY CONCERN (letter-spaced, underlined) ───
  doc.setFontSize(12);
  doc.setFont(FONT, 'bold');
  drawSpacedTitle(doc, 'TO WHOMSOEVER IT MAY CONCERN', { cx: pw / 2, y, charSpace: 2 });
  y += 14;

  // ── Certification Statement ───────────────────────────────────────────
  // The letter certifies the transport in one paragraph — no reference
  // block above it and no list below it — typed flush to the margins the
  // letterhead and the signature sit on, both edges justified the way the
  // certificate is typed, and advancing by the height of the lines it drew.
  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');

  const bodyText = sv(ed.body_text, '');
  if (bodyText.trim()) {
    doc.text(bodyText, M, y, { align: 'justify', maxWidth: cw });
    const bodyLines = doc.splitTextToSize(bodyText, cw);
    y += bodyLines.length * doc.getLineHeightFactor() * doc.getFontSize() * MM_PER_PT;
  }
  y += 25;

  // ── Signature Section ─────────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');
  const forLabel = 'FOR, ';
  doc.text(forLabel, M, y);
  const forLabelWidth = doc.getTextWidth(forLabel);
  doc.setFont(FONT, 'bold');
  doc.text(companyNameUpper, M + forLabelWidth, y);
  y += 18;

  doc.setFont(FONT, 'italic');
  doc.setFontSize(9);
  doc.text('AUTHORISED SIGNATORY.', M, y);

  // ── Page footer ───────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);

    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);

    // Left: Date
    doc.text(footerDate, M, ph - 5);

    // Center: Page number
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });

    // Right: Company name
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });

    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`TruckOperators_${sv(document.invoice_no, document.id)}.pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// TRUCK RECEIPT / CONSIGNMENT NOTE PDF — data overlay for the pre-printed
// JAIN PARIVAHAN lorry receipt form (A4 landscape), NON-NEGOTIABLE COPY (NNC)
// print run. Only the variable data is printed; borders, headings and boxes
// come from the pre-printed stationery, so each value must land exactly on
// its blank on the form.
//
// Field geometry lives in TRUCK_RECEIPT_NNC_GEO (mm, A4 landscape 297 × 210);
// the visual layout editor reads the same table through
// getTruckReceiptNNCFields() and its saved profiles are applied via the
// `layout` argument — see layoutGeo() in helpers.js for the profile shape.
// ═══════════════════════════════════════════════════════════════════════════════

export const TRUCK_RECEIPT_NNC_DOC_TYPE = 'truckReceipt';

const TRUCK_RECEIPT_NNC_GEO = {
  // ── Insurance box (centre) ──────────────────────────────────────────────
  insCompany:   { id: 'I1', label: 'Insurance company',        x: 134,   y: 89.8,  w: 48 },
  insPolicyNo:  { id: 'I2', label: 'Policy No.',               x: 131,   y: 95.2,  w: 38 },
  insPolicyDate:{ id: 'I3', label: 'Policy date',              x: 191,   y: 95.2,  w: 28 },
  insAmount:    { id: 'I4', label: 'Insured amount',           x: 129,   y: 100.5, w: 41 },
  insRisk:      { id: 'I5', label: 'Risk',                     x: 190,   y: 100.5, w: 28 },

  // ── Address of delivery office (top-right, three ruled lines) ───────────
  delivery:     { id: 'D',  label: 'Address of delivery office', x: 214.5, w: 70,
                  ys: [55.8, 63.2, 70.2] },

  state:        { id: 'S1', label: 'State',                    x: 232,   y: 75, w: 28 },
  telNo:        { id: 'S2', label: 'Tel No.',                  x: 270,   y: 75, w: 24 },

  // ── Consignment note No. / Date (right column) ──────────────────────────
  noteNo:       { id: 'N1', label: 'Consignment note No.',     x: 225,   y: 98,    w: 40 },
  noteDate:     { id: 'N2', label: 'Consignment date',         x: 225,   y: 105.6, w: 40 },

  // ── From / To boxes — one centred line, or two stacked when the place wraps
  from:         { id: 'F1', label: 'From (place)', x: 216, w: 68, y: 118.3, ys: [116.3, 120.3] },
  to:           { id: 'T1', label: 'To (place)',   x: 216, w: 68, y: 132.5, ys: [130.8, 134.8] },

  // ── Consignor / consignee-bank blocks: first line after the printed label,
  //    the rest flowing below in tight 2.8 mm lines (fixed 6.5 pt type — the
  //    NNC sheet leaves more room than the NCC one, so no size ladder).
  consignor:    { id: 'C',  label: "Consignor's name & address",
                  first: { x: 99,  y: 111.2, w: 114 },
                  rest:  { x: 48,  w: 160, ys: [116.6, 119.4, 122.2] } },
  bank:         { id: 'B',  label: "Consignee bank's name & address + NOTIFY",
                  first: { x: 107, y: 125.2, w: 104 },
                  rest:  { x: 48,  w: 160, ys: [130.4, 133.2, 136.0, 138.8, 141.6, 144.4] } },

  // ── Goods table ─────────────────────────────────────────────────────────
  packages:     { id: 'P1', label: 'Packages (printed bottom-to-top)', x: 54, y: 193, w: 40 },
  description:  { id: 'G',  label: 'Description / said to contain',
                  x: 67, y: 157, w: 95, lh: 3.3, rows: 11 },
  netWeight:    { id: 'W1', label: 'Total net weight',   x: 165, labelY: 158, valueY: 162, w: 32 },
  grossWeight:  { id: 'W2', label: 'Total gross weight', x: 165, labelY: 169, valueY: 173, w: 32 },

  value:        { id: 'V1', label: 'Value', x: 62, y: 201.5, w: 40 },
};

// Field list for the visual layout editor: one draggable entry per GEO key,
// with the bounding box (page mm) the field's text occupies at zero offset.
export function getTruckReceiptNNCFields() {
  const G = TRUCK_RECEIPT_NNC_GEO;
  const ASC = 3.4; // a text baseline's box rises this far above it
  const line = (g) => ({ x: g.x, y: g.y - ASC, w: g.w, h: ASC + 1.2 });
  const lines = (g) => ({
    x: g.x, y: g.ys[0] - ASC, w: g.w,
    h: g.ys[g.ys.length - 1] - g.ys[0] + ASC + 1.2,
  });
  const flow = (g) => {
    const left = Math.min(g.first.x, g.rest.x);
    const right = Math.max(g.first.x + g.first.w, g.rest.x + g.rest.w);
    const bottom = g.rest.ys[g.rest.ys.length - 1];
    return { x: left, y: g.first.y - ASC, w: right - left, h: bottom - g.first.y + ASC + 1.2 };
  };
  const weight = (g) => ({ x: g.x, y: g.labelY - 3, w: g.w, h: g.valueY - g.labelY + 4.5 });
  const F = (key, box) => ({ key, id: G[key].id, label: G[key].label, box });
  return [
    F('insCompany', line(G.insCompany)),
    F('insPolicyNo', line(G.insPolicyNo)),
    F('insPolicyDate', line(G.insPolicyDate)),
    F('insAmount', line(G.insAmount)),
    F('insRisk', line(G.insRisk)),
    F('delivery', lines(G.delivery)),
    F('state', line(G.state)),
    F('telNo', line(G.telNo)),
    F('noteNo', line(G.noteNo)),
    F('noteDate', line(G.noteDate)),
    F('from', lines(G.from)),
    F('to', lines(G.to)),
    F('consignor', flow(G.consignor)),
    F('bank', flow(G.bank)),
    F('packages', { x: G.packages.x - 2, y: G.packages.y - G.packages.w, w: 5, h: G.packages.w }),
    F('description', {
      x: G.description.x, y: G.description.y - G.description.lh,
      w: G.description.w, h: G.description.rows * G.description.lh,
    }),
    F('netWeight', weight(G.netWeight)),
    F('grossWeight', weight(G.grossWeight)),
    F('value', line(G.value)),
  ];
}

export function generateTruckReceiptPDF(document, company, authority, returnBlob = false, layout = null) {
  const doc = new jsPDF('l', 'mm', 'a4'); // form is landscape (297 × 210 mm)

  const data = buildTruckReceiptData(document, company);

  const EG = layoutGeo(TRUCK_RECEIPT_NNC_GEO, layout);
  const show = (key) => !layoutFieldHidden(layout, key);

  // Calibration offsets (mm) from the active layout profile. If the printer
  // feeds the pre-printed form slightly off, shift everything at once:
  // +X moves right, +Y moves down.
  const OX = (layout && layout.ox) || 0;
  const OY = (layout && layout.oy) || 0;
  const put = (text, x, y, opts) => {
    const t = sv(text);
    if (t) doc.text(t, x + OX, y + OY, opts);
  };

  doc.setTextColor(0, 0, 0);
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);

  // Demurrage days/rate are pre-printed on the form — nothing to overlay.

  // ── Insurance box (centre) ────────────────────────────────────────────
  // No checkbox mark is printed (the reference TR copy leaves both boxes
  // blank); insurance details print only when the consignment is insured.
  if (data.insured) {
    doc.setFont(FONT, 'normal');
    doc.setFontSize(8);
    if (show('insCompany')) put(data.insurance_company, EG.insCompany.x, EG.insCompany.y);
    if (show('insPolicyNo')) put(data.policy_no, EG.insPolicyNo.x, EG.insPolicyNo.y);
    if (show('insPolicyDate')) put(formatDate(data.policy_date), EG.insPolicyDate.x, EG.insPolicyDate.y);
    if (show('insAmount')) put(data.insurance_amount, EG.insAmount.x, EG.insAmount.y);
    if (show('insRisk')) put(data.risk, EG.insRisk.x, EG.insRisk.y);
  }
  doc.setFont(FONT, 'normal');
  doc.setFontSize(8);

  // ── Address of delivery office (top-right, three ruled lines) ─────────
  if (show('delivery')) {
    const deliveryLines = doc.splitTextToSize(sv(data.delivery_office), EG.delivery.w);
    EG.delivery.ys.forEach((ly, i) => {
      if (deliveryLines[i]) put(deliveryLines[i], EG.delivery.x, ly);
    });
  }

  // ── State / Tel No. ───────────────────────────────────────────────────
  if (show('state')) put(data.state, EG.state.x, EG.state.y);
  if (show('telNo')) put(data.tel_no, EG.telNo.x, EG.telNo.y);

  // ── Consignment note No. / Date / From / To (right column) ────────────
  doc.setFont(FONT, 'bold');
  doc.setFontSize(11);
  if (show('noteNo')) put(data.consignment_note_no, EG.noteNo.x, EG.noteNo.y); // "No. ______"
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  if (show('noteDate')) put(formatDate(data.consignment_date), EG.noteDate.x, EG.noteDate.y); // "Date ______"

  // From / To boxes (two lines fit)
  const placeBox = (text, g) => {
    const lines = doc.splitTextToSize(sv(text), g.w);
    if (lines.length <= 1) {
      put(lines[0], g.x, g.y);
    } else {
      put(lines[0], g.x, g.ys[0]);
      put(lines[1], g.x, g.ys[1]);
    }
  };
  if (show('from')) placeBox(data.from_place, EG.from);
  if (show('to')) placeBox(data.to_place, EG.to);

  // ── Consignor's Name & Address ────────────────────────────────────────
  // Full company block (name, address, e-mail, GSTIN) in small type: first
  // line after the label, the rest flowing below in tight lines — matching
  // the reference TR copy, which never truncates these details.
  doc.setFontSize(6.5);
  if (show('consignor')) {
    const consignorAll = doc.splitTextToSize(sv(data.consignor), EG.consignor.first.w);
    put(consignorAll[0], EG.consignor.first.x, EG.consignor.first.y);
    if (consignorAll.length > 1) {
      const rest = doc.splitTextToSize(consignorAll.slice(1).join(' '), EG.consignor.rest.w);
      rest.slice(0, EG.consignor.rest.ys.length)
        .forEach((line, i) => put(line, EG.consignor.rest.x, EG.consignor.rest.ys[i]));
    }
  }

  // ── Consignee Bank's Name & Address + NOTIFY ──────────────────────────
  // "TO THE ORDER OF <bank>" followed by "NOTIFY: <buyer> AND <bank>",
  // flowing down into the space above the table like the reference copy.
  if (show('bank')) {
    const bankText = [
      sv(data.consignee_bank),
      data.notify ? `NOTIFY: ${sv(data.notify)}` : '',
    ].filter(Boolean).join(' ');
    const bankAll = doc.splitTextToSize(bankText, EG.bank.first.w);
    put(bankAll[0], EG.bank.first.x, EG.bank.first.y);
    if (bankAll.length > 1) {
      const rest = doc.splitTextToSize(bankAll.slice(1).join(' '), EG.bank.rest.w);
      rest.slice(0, EG.bank.rest.ys.length)
        .forEach((line, i) => put(line, EG.bank.rest.x, EG.bank.rest.ys[i]));
    }
  }

  // ── Main table body ───────────────────────────────────────────────────
  // Packages column — printed vertically (bottom-to-top) like the manual TRs
  doc.setFontSize(9);
  if (show('packages')) put(sv(data.packages), EG.packages.x, EG.packages.y, { angle: 90 });

  // Description (said to contain)
  if (show('description')) {
    doc.setFontSize(7.5);
    const descLines = doc.splitTextToSize(sv(data.description), EG.description.w);
    descLines.slice(0, EG.description.rows).forEach((line, i) => {
      put(line, EG.description.x, EG.description.y + i * EG.description.lh);
    });
  }

  // Weight — labelled net / gross totals across the weight columns,
  // exactly as handwritten on the corrected print and in the reference copy
  doc.setFontSize(8);
  if (show('netWeight')) {
    doc.setFont(FONT, 'bold');
    put('TOTAL NET WEIGHT', EG.netWeight.x, EG.netWeight.labelY);
    doc.setFont(FONT, 'normal');
    put(sv(data.actual_weight), EG.netWeight.x, EG.netWeight.valueY);
  }
  if (show('grossWeight')) {
    doc.setFont(FONT, 'bold');
    put('TOTAL GROSS WEIGHT', EG.grossWeight.x, EG.grossWeight.labelY);
    doc.setFont(FONT, 'normal');
    put(sv(data.charged_weight), EG.grossWeight.x, EG.grossWeight.valueY);
  }

  // ── Value (bottom-left, clear of the pre-printed "Value" label) ───────
  if (show('value')) {
    doc.setFont(FONT, 'bold');
    doc.setFontSize(10);
    put(data.value, EG.value.x, EG.value.y);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`TruckReceipt_${sv(document.invoice_no, document.id)}.pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// L/C TERMS & CONDITIONS COMPLIANCE CERTIFICATE PDF - Official Format
// ═══════════════════════════════════════════════════════════════════════════════

export function generateLCCompliancePDF(document, company, authority, returnBlob = false) {
  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, 'lcCompliance');
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2;

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  let y = M + 5;

  // ── Company Letterhead ────────────────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont(FONT, 'bold');
  doc.text(sv(company.exporter_name, '').toUpperCase(), M, y);
  y += 6;

  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');
  const headerAddr = sv(company.exporter_address) +
    (company.email ? ` E MAIL:${sv(company.email)},` : '');
  const addrLines = doc.splitTextToSize(headerAddr, cw * 0.6);
  doc.text(addrLines, M, y);
  y += addrLines.length * 4;

  if (company.gstin) {
    doc.text(`GSTIN: ${sv(company.gstin)}`, M, y);
    y += 4;
  }
  doc.text(`IEC NO: ${sv(company.iec)}`, M, y);
  y += 6;

  // Horizontal line
  doc.line(M, y, pw - M, y);
  y += 8;

  // ── Date (right aligned) ──────────────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  doc.text(ed.date_line, pw - M, y, { align: 'right' });
  y += 12;

  // ── Title (centered, underlined) ──────────────────────────────────────
  doc.setFont(FONT, 'bold');
  doc.setFontSize(11);
  const title = 'TO WHOMSOEVER IT MAY CONCERN';
  const titleWidth = doc.getTextWidth(title);
  const titleX = (pw - titleWidth) / 2;
  doc.text(title, titleX, y);
  doc.line(titleX, y + 1, titleX + titleWidth, y + 1);
  y += 15;

  // ── Certification Statement ───────────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(10);

  const certStatement = ed.cert_statement;

  doc.text(certStatement, M, y, { align: 'justify', maxWidth: cw });
  const certLines = doc.splitTextToSize(certStatement, cw);
  const lineHeight = doc.getLineHeightFactor() * doc.getFontSize() * 0.3528;
  y += certLines.length * lineHeight;

  // ── Signature (positioned near bottom) ─────────────────────────────────
  const sigY = Math.max(y + 20, ph - 65);
  doc.setFont(FONT, 'normal');
  doc.setFontSize(10);
  doc.text('FOR,', M, sigY);
  doc.setFont(FONT, 'bold');
  doc.text(` ${sv(company.exporter_name, '').toUpperCase()}`, M + doc.getTextWidth('FOR, '), sigY);

  doc.setFont(FONT, 'normal');
  doc.text('AUTHORISED SIGNATORY.', M, sigY + 20);

  // ── Page footer ───────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);

    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);

    // Left: Date
    doc.text(footerDate, M, ph - 5);

    // Center: Page number
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });

    // Right: Company name
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });

    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`LCCompliance_${sv(document.invoice_no, document.id)}.pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// IMPORTER'S NAME, ADDRESS & TIN NO. PRINTED CERTIFICATE PDF
// ═══════════════════════════════════════════════════════════════════════════════

export function generateImporterNameAddressPDF(document, company, authority, returnBlob = false) {
  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, 'importerNameAddress');
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2;

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  let y = M + 5;

  // ── Company Letterhead ────────────────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont(FONT, 'bold');
  doc.text(sv(company.exporter_name, '').toUpperCase(), M, y);
  y += 6;

  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');
  const headerAddr = sv(company.exporter_address) +
    (company.email ? ` E MAIL:${sv(company.email)},` : '');
  const addrLines = doc.splitTextToSize(headerAddr, cw * 0.6);
  doc.text(addrLines, M, y);
  y += addrLines.length * 4;
  if (company.gstin) {
    doc.text(`GSTIN: ${sv(company.gstin)}`, M, y);
    y += 4;
  }
  doc.text(`IEC NO: ${sv(company.iec)}`, M, y);
  y += 6;

  // Horizontal line
  doc.line(M, y, pw - M, y);
  y += 8;

  // ── Date (right aligned, italic) ──────────────────────────────────────
  doc.setFont(FONT, 'italic');
  doc.setFontSize(9);
  doc.text(ed.date_line, pw - M, y, { align: 'right' });
  y += 12;

  // ── Title (centered, underlined) ──────────────────────────────────────
  doc.setFont(FONT, 'bold');
  doc.setFontSize(11);
  const title = 'TO WHOMSOEVER IT MAY CONCERN';
  const titleWidth = doc.getTextWidth(title);
  const titleX = (pw - titleWidth) / 2;
  doc.text(title, titleX, y);
  doc.line(titleX, y + 1, titleX + titleWidth, y + 1);
  y += 14;

  // ── Certification Statement ───────────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);

  const certStatement = ed.cert_statement;

  // Both edges flush, the way the letter is typed; jsPDF leaves the last
  // line of the paragraph ragged on its own.
  doc.text(certStatement, M, y, { align: 'justify', maxWidth: cw });
  const certLines = doc.splitTextToSize(certStatement, cw);
  y += certLines.length * doc.getLineHeightFactor() * doc.getFontSize() * MM_PER_PT;

  // The signature follows the paragraph the way the typed letter spaces it —
  // one clear gap below the text rather than pinned to the foot of the page,
  // where a tall preview pane cuts it off. A long statement pushes it down
  // only as far as the page footer allows.
  y = Math.min(y + 25, ph - 45);

  // ── Signature ─────────────────────────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  const forText = 'FOR, ';
  doc.text(forText, M, y);
  const forWidth = doc.getTextWidth(forText);
  doc.setFont(FONT, 'bold');
  doc.text(sv(company.exporter_name, '').toUpperCase(), M + forWidth, y);
  y += 20;

  doc.setFont(FONT, 'normal');
  doc.text('AUTHORISED SIGNATORY.', M, y);

  // ── Page footer ───────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);

    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);

    // Left: Date
    doc.text(footerDate, M, ph - 5);

    // Center: Page number
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });

    // Right: Company name
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });

    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`ImporterNameAddress_${sv(document.invoice_no, document.id)}.pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// PRE-SHIPMENT INSPECTION CERTIFICATE PDF
// ═══════════════════════════════════════════════════════════════════════════════

export function generatePreShipmentInspectionPDF(document, company, authority, returnBlob = false) {
  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, 'preShipmentInspection');
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2;

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  let y = M + 5;

  // ── Company Letterhead ────────────────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont(FONT, 'bold');
  doc.text(sv(company.exporter_name, '').toUpperCase(), M, y);
  y += 6;

  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');
  let addrText = sv(company.exporter_address);
  if (company.email) {
    addrText += ` E MAIL:${sv(company.email)},`;
  }
  const addrLines = doc.splitTextToSize(addrText, cw * 0.6);
  doc.text(addrLines, M, y);
  y += addrLines.length * 4 + 2;

  if (company.gstin) {
    doc.text(`GSTIN: ${sv(company.gstin)}`, M, y);
    y += 4;
  }
  doc.text(`IEC NO: ${sv(company.iec)}`, M, y);
  y += 6;

  // Horizontal line
  doc.line(M, y, pw - M, y);
  y += 8;

  // ── Date (right aligned, document date) ───────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  doc.text(ed.date_line, pw - M, y, { align: 'right' });
  y += 12;

  // ── Title (centered, bold, underlined) ────────────────────────────────
  doc.setFont(FONT, 'bold');
  doc.setFontSize(12);
  const title = 'PRE-SHIPMENT INSPECTION CERTIFICATE';
  const titleWidth = doc.getTextWidth(title);
  const titleX = (pw - titleWidth) / 2;
  doc.text(title, titleX, y);
  doc.line(titleX, y + 1, titleX + titleWidth, y + 1);
  y += 15;

  // ── Certification Statement ───────────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(11);

  const certStatement = ed.cert_statement;

  doc.setLineHeightFactor(1.5);
  doc.text(certStatement, M, y, { align: 'justify', maxWidth: cw });
  doc.setLineHeightFactor(1.15);

  // ── Signature (near bottom of page) ───────────────────────────────────
  y = ph - 40;
  doc.setFont(FONT, 'bold');
  doc.setFontSize(9);
  doc.text(`FOR, ${sv(company.exporter_name, '').toUpperCase()}`, M, y);
  y += 15;

  doc.setFont(FONT, 'normal');
  doc.text('AUTHORISED SIGNATORY.', M, y);

  // ── Page footer ───────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);

    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);

    // Left: Date
    doc.text(footerDate, M, ph - 5);

    // Center: Page number
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });

    // Right: Company name
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });

    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`PreShipmentInspection_${sv(document.invoice_no, document.id)}.pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// WEIGHT AND MEASUREMENT LIST PDF
// ═══════════════════════════════════════════════════════════════════════════════

export function generateWeightMeasurementPDF(document, company, authority, returnBlob = false) {
  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, 'weightMeasurement');
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2;

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  let y = M + 5;

  // ── Company Letterhead ────────────────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont(FONT, 'bold');
  doc.text(sv(company.exporter_name, '').toUpperCase(), M, y);
  y += 6;

  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');
  const addrLines = doc.splitTextToSize(sv(company.exporter_address), cw * 0.6);
  doc.text(addrLines, M, y);
  y += addrLines.length * 4 + 2;

  if (company.email) {
    doc.text(`MAIL:${sv(company.email)},`, M, y);
    y += 4;
  }
  if (company.gstin) {
    doc.text(`GSTIN: ${sv(company.gstin)}`, M, y);
    y += 4;
  }
  doc.text(`IEC NO: ${sv(company.iec)}`, M, y);
  y += 6;

  // Horizontal line
  doc.line(M, y, pw - M, y);
  y += 8;

  // ── Date (right aligned) ──────────────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  doc.text(ed.date_line, pw - M, y, { align: 'right' });
  y += 10;

  // ── Title (centered, bold, underlined) ────────────────────────────────
  doc.setFont(FONT, 'bold');
  doc.setFontSize(11);
  const title = 'WEIGHT AND MEASURMENT LIST';
  const titleWidth = doc.getTextWidth(title);
  const titleX = (pw - titleWidth) / 2;
  doc.text(title, titleX, y);
  doc.line(titleX, y + 1, titleX + titleWidth, y + 1);
  y += 12;

  // ── Numbered Detail Rows ──────────────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);

  const labelX = M;
  const colonX = M + 78;
  const valueX = M + 82;
  const valueW = cw - 82;

  // 01. Invoice No. and Date
  doc.setFont(FONT, 'bold');
  doc.text('01. INVOICE NO. AND DATE', labelX, y);
  doc.setFont(FONT, 'normal');
  doc.text(':', colonX, y);
  doc.text(ed.invoice_line, valueX, y);
  y += 8;

  // 02. Buyer Contract No. and Date
  doc.setFont(FONT, 'bold');
  doc.text('02. BUYER CONTRACT NO. AND DATE', labelX, y);
  doc.setFont(FONT, 'normal');
  doc.text(':', colonX, y);
  const buyerContractVal = ed.buyer_contract;
  const buyerContractLines = doc.splitTextToSize(buyerContractVal, valueW);
  doc.text(buyerContractLines, valueX, y);
  y += buyerContractLines.length * 4 + 4;

  // 03. L/C No. and Date
  doc.setFont(FONT, 'bold');
  doc.text('03. L/C NO. AND DATE', labelX, y);
  doc.setFont(FONT, 'normal');
  doc.text(':', colonX, y);
  const lcVal = ed.lc_line;
  const lcLines = doc.splitTextToSize(lcVal, valueW);
  doc.text(lcLines, valueX, y);
  y += lcLines.length * 4 + 4;

  // 04. Commodity and Quantity
  doc.setFont(FONT, 'bold');
  doc.text('04. COMMODITY AND QUANTITY', labelX, y);
  doc.setFont(FONT, 'normal');
  doc.text(':', colonX, y);
  const commodityVal = ed.commodity_line;
  const commodityLines = doc.splitTextToSize(commodityVal, valueW);
  doc.text(commodityLines, valueX, y);
  y += commodityLines.length * 4 + 4;

  // 05. Net Weight and Gross Weight
  doc.setFont(FONT, 'bold');
  doc.text('05. NET WEIGHT AND GROSS WEIGHT', labelX, y);
  doc.setFont(FONT, 'normal');
  doc.text(':', colonX, y);
  doc.text(ed.net_weight_line, valueX, y);
  y += 4;
  doc.text(ed.gross_weight_line, valueX, y);
  y += 8;

  // 06. Total Packages
  doc.setFont(FONT, 'bold');
  doc.text('06. TOTAL PACKAGES', labelX, y);
  doc.setFont(FONT, 'normal');
  doc.text(':', colonX, y);
  doc.text(ed.packages_line, valueX, y);
  y += 8;

  // 07. Consignee/Buyer/Applicant
  doc.setFont(FONT, 'bold');
  doc.text('07. CONSIGNEE/ BUYER/ APPLICANT', labelX, y);
  doc.setFont(FONT, 'normal');
  doc.text(':', colonX, y);
  const buyerVal = ed.buyer_block;
  const buyerLines = doc.splitTextToSize(buyerVal, valueW);
  doc.text(buyerLines, valueX, y);
  y += buyerLines.length * 4 + 4;

  // 08. Other Ref.
  doc.setFont(FONT, 'bold');
  doc.text('08. OTHER REF.', labelX, y);
  doc.setFont(FONT, 'normal');
  doc.text(':', colonX, y);
  const otherRefVal = ed.other_ref;
  const otherRefLines = doc.splitTextToSize(otherRefVal, valueW);
  doc.text(otherRefLines, valueX, y);
  y += otherRefLines.length * 4 + 6;

  // ── Certification Statement ───────────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  const certStatement = ed.cert_statement;
  const certLines = doc.splitTextToSize(certStatement, cw);
  doc.text(certLines, M, y);
  y += certLines.length * 4 + 35;

  // ── Signature ─────────────────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');
  const forText = 'FOR, ';
  doc.text(forText, M, y);
  const forWidth = doc.getTextWidth(forText);
  doc.setFont(FONT, 'bold');
  doc.text(sv(company.exporter_name, '').toUpperCase(), M + forWidth, y);
  y += 20;

  doc.setFont(FONT, 'bold');
  doc.text('AUTHORISED SIGNATORY.', M, y);

  // ── Page footer ───────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);

    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);

    // Left: Date
    doc.text(footerDate, M, ph - 5);

    // Center: Page number
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });

    // Right: Company name
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });

    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`WeightMeasurement_${sv(document.invoice_no, document.id)}.pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// FAX TRANSMISSION PDF
// ═══════════════════════════════════════════════════════════════════════════════

export function generateFaxTransmissionPDF(document, company, authority, returnBlob = false) {
  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, 'faxTransmission');
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2;

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  let y = M + 5;

  // ── Company Letterhead ────────────────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont(FONT, 'bold');
  doc.text(sv(company.exporter_name, '').toUpperCase(), M, y);
  y += 6;

  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');
  const addrLines = doc.splitTextToSize(sv(company.exporter_address), cw * 0.6);
  doc.text(addrLines, M, y);
  y += addrLines.length * 4 + 2;

  if (company.email) {
    doc.text(`MAIL:${sv(company.email)},`, M, y);
    y += 4;
  }
  if (company.gstin) {
    doc.text(`GSTIN: ${sv(company.gstin)}`, M, y);
    y += 4;
  }
  doc.text(`IEC NO: ${sv(company.iec)}`, M, y);
  y += 6;

  // Horizontal line
  doc.line(M, y, pw - M, y);
  y += 8;

  // ── Date (right aligned, document date) ───────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  doc.text(ed.date_line, pw - M, y, { align: 'right' });
  y += 10;

  // ── Title (centered, bold, underlined) ────────────────────────────────
  doc.setFont(FONT, 'bold');
  doc.setFontSize(11);
  const title = 'F A X   T R A N S M I S S I O N';
  const titleWidth = doc.getTextWidth(title);
  const titleX = (pw - titleWidth) / 2;
  doc.text(title, titleX, y);
  doc.line(titleX, y + 1, titleX + titleWidth, y + 1);
  y += 10;

  // ── TO Section (LC Opening Bank) ──────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  doc.text('TO,', M, y);
  y += 4;

  const toLines = doc.splitTextToSize(ed.to_block, cw * 0.6);
  doc.text(toLines, M, y);
  y += toLines.length * 4 + 6;

  // ── REF. NO. Section (underlined) ─────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  const refText = ed.ref_text;
  const refLines = doc.splitTextToSize(refText, cw);
  doc.text(refLines, M, y);
  // Underline the REF section
  const refEndY = y + (refLines.length - 1) * 4;
  for (let r = 0; r < refLines.length; r++) {
    const lineY = y + r * 4;
    const lineW = doc.getTextWidth(refLines[r]);
    doc.line(M, lineY + 1, M + lineW, lineY + 1);
  }
  y += refLines.length * 4 + 8;

  // ── Salutation ────────────────────────────────────────────────────────
  doc.text('DEAR SIR,', M, y);
  y += 4;

  const bodyText = ed.body_text;
  const bodyLines = doc.splitTextToSize(bodyText, cw);
  doc.text(bodyLines, M, y);
  y += bodyLines.length * 4 + 6;

  // ── Numbered Detail Rows ──────────────────────────────────────────────
  const labelX = M;
  const colonX = M + 78;
  const valueX = M + 82;
  const valueW = cw - 82;

  // 01. Shipment Date and Carrier Details
  doc.text('01. SHIPMENT DATE AND CARRIER DETAILS', labelX, y);
  doc.text(':', colonX, y);
  const carrierVal = ed.carrier_line;
  const carrierLines = doc.splitTextToSize(carrierVal, valueW);
  doc.text(carrierLines, valueX, y);
  y += carrierLines.length * 4 + 4;

  // 02. Consignee/Buyer/Applicant
  doc.text('02. CONSIGNEE/ BUYER/ APPLICANT', labelX, y);
  doc.text(':', colonX, y);
  const buyerVal = ed.buyer_block;
  const buyerLines = doc.splitTextToSize(buyerVal, valueW);
  doc.text(buyerLines, valueX, y);
  y += buyerLines.length * 4 + 4;

  // 03. Invoice No. & Date
  doc.text('03. INVOICE NO. & DATE', labelX, y);
  doc.text(':', colonX, y);
  doc.text(ed.invoice_line, valueX, y);
  y += 8;

  // 04. Amount of Bill
  doc.text('04. AMOUNT OF BILL', labelX, y);
  doc.text(':', colonX, y);
  const amountVal = ed.amount_line;
  const amountLines = doc.splitTextToSize(amountVal, valueW);
  doc.text(amountLines, valueX, y);
  y += amountLines.length * 4 + 4;

  // 05. Commodity and Quantity
  doc.text('05. COMMODITY AND QUANTITY', labelX, y);
  doc.text(':', colonX, y);
  const commodityVal = ed.commodity_line;
  const commodityLines = doc.splitTextToSize(commodityVal, valueW);
  doc.text(commodityLines, valueX, y);
  y += commodityLines.length * 4 + 4;

  // 06. Net Weight and Gross Weight
  doc.text('06. NET WEIGHT AND GROSS WEIGHT', labelX, y);
  doc.text(':', colonX, y);
  doc.text(ed.net_weight_line, valueX, y);
  y += 4;
  doc.text(ed.gross_weight_line, valueX, y);
  y += 8;

  // 07. Total Packages
  doc.text('07. TOTAL PACKAGES', labelX, y);
  doc.text(':', colonX, y);
  doc.text(ed.packages_line, valueX, y);
  y += 8;

  // ── Other Ref (full width) ────────────────────────────────────────────
  const otherRefText = ed.other_ref;
  const otherRefLines = doc.splitTextToSize(otherRefText, cw);
  doc.text(otherRefLines, M, y);
  y += otherRefLines.length * 4 + 8;

  // Horizontal line before signature
  doc.line(M, y, pw - M, y);
  y += 10;

  // ── Signature ─────────────────────────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  doc.text('FOR, ', M, y);
  const forWidth = doc.getTextWidth('FOR, ');
  doc.setFont(FONT, 'bold');
  doc.text(sv(company.exporter_name, '').toUpperCase(), M + forWidth, y);
  y += 15;

  doc.setFont(FONT, 'normal');
  doc.text('AUTHORISED SIGNATORY.', M, y);

  // ── Page footer ───────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);

    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);

    doc.text(footerDate, M, ph - 5);
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });

    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`FaxTransmission_${sv(document.invoice_no, document.id)}.pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// SHIPMENT ADVICE TO THE OPENING BANK PDF
// ═══════════════════════════════════════════════════════════════════════════════

export function generateShipmentAdvicePDF(document, company, authority, returnBlob = false) {
  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, 'shipmentAdvice');
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2;

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  let y = M + 5;

  // ── Company Letterhead ────────────────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont(FONT, 'bold');
  doc.text(sv(company.exporter_name, '').toUpperCase(), M, y);
  y += 6;

  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');
  const addrLines = doc.splitTextToSize(sv(company.exporter_address), cw * 0.6);
  doc.text(addrLines, M, y);
  y += addrLines.length * 4 + 2;

  if (company.email) {
    doc.text(`MAIL:${sv(company.email)},`, M, y);
    y += 4;
  }
  if (company.gstin) {
    doc.text(`GSTIN: ${sv(company.gstin)}`, M, y);
    y += 4;
  }
  doc.text(`IEC NO: ${sv(company.iec)}`, M, y);
  y += 6;

  // Horizontal line
  doc.line(M, y, pw - M, y);
  y += 8;

  // ── Date (right aligned, document date) ───────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  doc.text(ed.date_line, pw - M, y, { align: 'right' });
  y += 10;

  // ── Title (centred on the width the spaced letters occupy) ────────────
  doc.setFont(FONT, 'bold');
  doc.setFontSize(11);
  drawSpacedTitle(doc, 'SHIPMENT ADVICE', { cx: pw / 2, y, charSpace: 2 });
  y += 10;

  // ── TO Section (the bank that opened the credit) ──────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  const lh = doc.getLineHeightFactor() * doc.getFontSize() * MM_PER_PT;
  doc.text('TO,', M, y);
  y += 4;

  const toLines = doc.splitTextToSize(ed.to_block, cw * 0.6);
  doc.text(toLines, M, y);
  y += toLines.length * lh + 8;

  // ── REF. NO. block (underlined) ───────────────────────────────────────
  // The credit is typed flush to both margins, so every line but the last is
  // justified to the right margin and ruled the whole width; the last is
  // ruled to where it ends. A document with no credit prints no bare label.
  const refText = sv(ed.ref_text).trim();
  if (refText) {
    const refLines = doc.splitTextToSize(refText, cw);
    doc.text(refText, M, y, { align: 'justify', maxWidth: cw });
    refLines.forEach((line, i) => {
      const ruleY = y + i * lh + 1;
      const endX = i === refLines.length - 1 ? M + doc.getTextWidth(line) : pw - M;
      doc.line(M, ruleY, endX, ruleY);
    });
    y += refLines.length * lh + 8;
  }

  // ── Salutation ────────────────────────────────────────────────────────
  doc.text('DEAR SIR,', M, y);
  y += 4;

  // ── Opening paragraph (both edges justified) ──────────────────────────
  const bodyText = sv(ed.body_text);
  const bodyLines = doc.splitTextToSize(bodyText, cw);
  doc.text(bodyText, M, y, { align: 'justify', maxWidth: cw });
  y += bodyLines.length * lh + 6;

  // ── Numbered Detail Rows ──────────────────────────────────────────────
  const labelX = M;
  const colonX = M + 78;
  const valueX = M + 82;
  const valueW = cw - 82;

  // A row is its label, the colon column and however many lines its value
  // wraps to; the next row starts below whichever of the two ran longer.
  const row = (label, value, gapBelow = 4) => {
    doc.text(label, labelX, y);
    doc.text(':', colonX, y);
    const valueLines = doc.splitTextToSize(sv(value), valueW);
    doc.text(valueLines, valueX, y);
    y += valueLines.length * lh + gapBelow;
  };

  row('01. SHIPMENT DATE AND CARRIER DETAILS', ed.carrier_line);
  row('02. CONSIGNEE/ BUYER/ APPLICANT', ed.buyer_block);
  row('03. INVOICE NO. & DATE', ed.invoice_line);
  row('04. AMOUNT OF BILL', ed.amount_line);
  row('05. COMMODITY AND QUANTITY', ed.commodity_line);
  // The two weights are typed one under the other against a single label
  row('06. NET WEIGHT AND GROSS WEIGHT', `${sv(ed.net_weight_line)}\n${sv(ed.gross_weight_line)}`);
  row('07. TOTAL PACKAGES', ed.packages_line, 8);

  // ── H.S. code and the importer's registration numbers (full width) ────
  const otherRef = sv(ed.other_ref).trim();
  if (otherRef) {
    const otherRefLines = doc.splitTextToSize(otherRef, cw);
    doc.text(otherRef, M, y, { align: 'justify', maxWidth: cw });
    y += otherRefLines.length * lh + 14;
  }

  // ── Signature ─────────────────────────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  const forText = 'FOR, ';
  doc.text(forText, M, y);
  doc.setFont(FONT, 'bold');
  doc.text(sv(company.exporter_name, '').toUpperCase(), M + doc.getTextWidth(forText), y);
  y += 15;

  doc.setFont(FONT, 'normal');
  doc.text('AUTHORISED SIGNATORY.', M, y);

  // ── Page footer ───────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);

    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);

    doc.text(footerDate, M, ph - 5);
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });

    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`ShipmentAdvice_${sv(document.invoice_no, document.id)}.pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// BENEFICIARY CERTIFICATE / SHIPMENT ADVICE TO INSURANCE COMPANY PDF
// ═══════════════════════════════════════════════════════════════════════════════

export function generateShipmentAdviceInsurancePDF(document, company, authority, returnBlob = false) {
  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, 'shipmentAdviceInsurance');
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2;

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  let y = M + 5;

  // ── Company Letterhead ────────────────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont(FONT, 'bold');
  doc.text(sv(company.exporter_name, '').toUpperCase(), M, y);
  y += 6;

  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');
  const addrLines = doc.splitTextToSize(sv(company.exporter_address), cw * 0.6);
  doc.text(addrLines, M, y);
  y += addrLines.length * 4 + 2;

  if (company.email) {
    doc.text(`MAIL:${sv(company.email)},`, M, y);
    y += 4;
  }
  if (company.gstin) {
    doc.text(`GSTIN: ${sv(company.gstin)}`, M, y);
    y += 4;
  }
  doc.text(`IEC NO: ${sv(company.iec)}`, M, y);
  y += 6;

  // Horizontal line
  doc.line(M, y, pw - M, y);
  y += 8;

  // ── Date (right aligned, document date) ───────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  doc.text(ed.date_line, pw - M, y, { align: 'right' });
  y += 6;

  // ── Title (centered, bold, underlined, two lines) ─────────────────────
  doc.setFont(FONT, 'bold');
  doc.setFontSize(11);

  const title1 = 'BENEFICIARY CERTIFICATE';
  const title1Width = doc.getTextWidth(title1);
  doc.text(title1, pw / 2, y, { align: 'center' });
  doc.line(pw / 2 - title1Width / 2, y + 1, pw / 2 + title1Width / 2, y + 1);
  y += 5;

  const title2 = 'SHIPMENT ADVICE';
  const title2Width = doc.getTextWidth(title2);
  doc.text(title2, pw / 2, y, { align: 'center' });
  doc.line(pw / 2 - title2Width / 2, y + 1, pw / 2 + title2Width / 2, y + 1);
  y += 8;

  // ── TO Section (Insurance Company) ────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  doc.text('TO,', M, y);
  y += 4;

  const toLines = doc.splitTextToSize(ed.to_block, cw * 0.5);
  doc.text(toLines, M, y);
  y += toLines.length * 4 + 6;

  // ── Salutation ────────────────────────────────────────────────────────
  doc.text('DEAR SIR,', M, y);
  y += 6;

  // ── Ref Section (indented, underlined block) ──────────────────────────
  const refIndent = M + 10;
  const refFirstX = refIndent + 12;
  doc.setFont(FONT, 'bold');
  doc.text('Ref :', refIndent, y);
  doc.setFont(FONT, 'normal');
  // One hanging paragraph, the way the letter is typed: only the opening line
  // clears the label, while the proforma and cover-note lines hang at the
  // block's left edge with the rest of the L/C wording. A ref line the invoice
  // has no data for is skipped rather than printed as a blank row.
  let refFirst = true;
  [ed.ref_line1, ed.ref_line2, ed.ref_line3].forEach((refLine) => {
    if (!sv(refLine).trim()) return;
    y += drawHangingParagraph(doc, refLine, {
      x: refIndent,
      firstX: refFirst ? refFirstX : refIndent,
      rightX: pw - M,
      y,
    });
    refFirst = false;
  });

  // Underline the entire ref block
  doc.line(refIndent, y, pw - M, y);
  y += 6;

  // ── Body paragraph ────────────────────────────────────────────────────
  doc.setFontSize(9);
  const bodyText = ed.body_text;
  const bodyLines = doc.splitTextToSize(bodyText, cw);
  doc.text(bodyLines, M, y, { align: 'justify', maxWidth: cw });
  y += bodyLines.length * 4 + 4;

  // ── Numbered Detail Rows ──────────────────────────────────────────────
  const labelX = M + 2;
  const colonX = M + 72;
  const valueX = M + 76;
  const valueW = cw - 76;

  // 01. Consignee/Buyer/Applicant
  doc.text('01. CONSIGNEE/ BUYER/ APPLICANT', labelX, y);
  doc.text(':', colonX, y);
  const buyerVal = ed.buyer_block;
  const buyerLines = doc.splitTextToSize(buyerVal, valueW);
  doc.text(buyerLines, valueX, y);
  y += buyerLines.length * 4 + 2;

  // 02. Invoice No. & Date
  doc.text('02. INVOICE NO. & DATE', labelX, y);
  doc.text(':', colonX, y);
  doc.text(ed.invoice_line, valueX, y);
  y += 6;

  // 03. Invoice Value
  doc.text('03. INVOICE VALUE', labelX, y);
  doc.text(':', colonX, y);
  doc.text(ed.invoice_value, valueX, y);
  y += 6;

  // 04. Description of Goods
  doc.text('04. DESCRIPTION OF GOODS', labelX, y);
  doc.text(':', colonX, y);
  const goodsLines = doc.splitTextToSize(ed.goods_line, valueW);
  doc.text(goodsLines, valueX, y);
  y += goodsLines.length * 4 + 2;

  // 05. Net Weight & Gross Weight
  doc.text('05. NET WEIGHT & GROSS WEIGHT', labelX, y);
  doc.text(':', colonX, y);
  doc.text(ed.weight_line, valueX, y);
  y += 6;

  // 06. Despatch Document No. & Date
  doc.text('06. DESPATCH DOCUMENT NO. &', labelX, y);
  y += 4;
  doc.text('DATE', labelX + 6, y);
  doc.text(':', colonX, y);
  doc.text(ed.despatch_line, valueX, y);
  y += 6;

  // 07. Carrier
  doc.text('07. CARRIER', labelX, y);
  doc.text(':', colonX, y);
  const carrierText = ed.carrier;
  const carrierLines = doc.splitTextToSize(carrierText, valueW);
  doc.text(carrierLines, valueX, y);
  y += carrierLines.length * 4 + 6;

  // ── Closing statement ─────────────────────────────────────────────────
  doc.text(ed.closing_line, M + 6, y);
  y += 10;

  // ── Formal closing ────────────────────────────────────────────────────
  doc.text('THANKING YOU,', M, y);
  y += 6;

  doc.text('YOURS FAITHFULLY,', M, y);
  y += 4;

  doc.setFont(FONT, 'bold');
  doc.text(`FOR, ${sv(company.exporter_name, '').toUpperCase()}`, M, y);
  y += 12;

  doc.setFont(FONT, 'normal');
  doc.text('AUTHORISED SIGNATORY.', M, y);

  // ── Page footer ───────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);

    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);

    doc.text(footerDate, M, ph - 5);
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });

    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`ShipmentAdviceInsurance_${sv(document.invoice_no, document.id)}.pdf`);
}

// ══════════════════════════════════════════════════════════════════════
// Non-Israeli Flag Vessel Certificate (no border)
// ══════════════════════════════════════════════════════════════════════
export function generateNonIsraeliFlagVesselPDF(document, company, authority, returnBlob = false) {
  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, 'nonIsraeliFlagVessel');
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2;

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  let y = M + 10;

  // ── Carrier Letterhead (centered, bold, large) ────────────────────────
  const carrierFull = ed.carrier_header;
  doc.setFont(FONT, 'bold');
  doc.setFontSize(18);
  const carrierHeaderLines = doc.splitTextToSize(carrierFull, cw * 0.55);
  carrierHeaderLines.forEach((line) => {
    doc.text(line, pw / 2, y, { align: 'center' });
    y += 8;
  });
  y += 2;

  // ── Horizontal line ───────────────────────────────────────────────────
  doc.setLineWidth(0.8);
  doc.line(M, y, pw - M, y);
  doc.setLineWidth(LW);
  y += 8;

  // ── Date (right-aligned) ──────────────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(10);
  doc.text(ed.date_line, pw - M, y, { align: 'right' });
  y += 14;

  // ── Title: TO WHOMSOEVER IT MAY CONCERN ───────────────────────────────
  doc.setFont(FONT, 'bold');
  doc.setFontSize(11);
  const title = 'TO WHOMSOEVER IT MAY CONCERN';
  drawSpacedTitle(doc, title, { cx: pw / 2, y, charSpace: 2 });
  y += 12;

  // ── Body paragraph ────────────────────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(10);

  const bodyText = ed.body_text;

  // The letter is typed flush to both margins, so every line but the last
  // is stretched to the full column width.
  const bodyLines = doc.splitTextToSize(bodyText, cw);
  doc.text(bodyLines, M, y, { align: 'justify', maxWidth: cw });
  y += bodyLines.length * 5 + 25;

  // ── Signature ─────────────────────────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(10);
  const forText = 'FOR, ';
  doc.text(forText, M, y);
  doc.setFont(FONT, 'bold');
  doc.text(ed.signed_for, M + doc.getTextWidth(forText), y);
  y += 16;

  doc.setFont(FONT, 'normal');
  doc.text('AUTHORISED SIGNATORY.', M, y);

  // ── Page footer ───────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);

    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);

    doc.text(footerDate, M, ph - 5);
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });

    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`NonIsraeliFlagVessel_${sv(document.invoice_no, document.id)}.pdf`);
}

// ══════════════════════════════════════════════════════════════════════
// Beneficiary's Certificate (no border)
// ══════════════════════════════════════════════════════════════════════
export function generateBeneficiaryCertificatePDF(document, company, authority, returnBlob = false) {
  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, 'beneficiaryCertificate');
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2;

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  let y = M + 5;

  // ── Company Letterhead ──────────────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont(FONT, 'bold');
  doc.text(sv(company.exporter_name, '').toUpperCase(), M, y);
  y += 6;

  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');
  let addrText = sv(company.exporter_address);
  if (company.email) {
    addrText += ` E MAIL:${sv(company.email)},`;
  }
  const addrLines = doc.splitTextToSize(addrText, cw * 0.6);
  doc.text(addrLines, M, y);
  y += addrLines.length * 4 + 2;

  if (company.gstin) {
    doc.text(`GSTIN: ${sv(company.gstin)}`, M, y);
    y += 4;
  }
  doc.text(`IEC NO: ${sv(company.iec)}`, M, y);
  y += 6;

  // Horizontal line
  doc.line(M, y, pw - M, y);
  y += 8;

  // ── Date (right aligned, current date) ──────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  doc.text(ed.date_line, pw - M, y, { align: 'right' });
  y += 10;

  // ── Title: BENEFICIARY'S CERTIFICATE ────────────────────────────────
  doc.setFont(FONT, 'bold');
  doc.setFontSize(11);
  const title1 = "BENEFICIARY'S CERTIFICATE";
  drawSpacedTitle(doc, title1, { cx: pw / 2, y, charSpace: 1.5 });
  y += 10;

  // ── Paragraphs ──────────────────────────────────────────────────────
  // No box frames either block, so both are typed flush to the margins the
  // letterhead and the signature sit on, both edges justified the way the
  // certificate is typed, and each advances by the height of the lines it
  // actually drew.
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);

  const typeParagraph = (text, gapBelow) => {
    const para = sv(text, '');
    // A paragraph the exporter cleared leaves no gap behind either
    if (!para.trim()) return;
    doc.text(para, M, y, { align: 'justify', maxWidth: cw });
    const lines = doc.splitTextToSize(para, cw);
    y += lines.length * doc.getLineHeightFactor() * doc.getFontSize() * MM_PER_PT + gapBelow;
  };

  // Reference paragraph — the invoice writes this one, it is never edited
  typeParagraph(ed.ref_text, 6);

  // ── Horizontal line separator ───────────────────────────────────────
  doc.line(M, y, pw - M, y);
  y += 8;

  // ── Title: TO WHOM IT MAY CONCERN ───────────────────────────────────
  doc.setFont(FONT, 'bold');
  doc.setFontSize(11);
  const title2 = 'TO WHOM IT MAY CONCERN';
  drawSpacedTitle(doc, title2, { cx: pw / 2, y, charSpace: 2 });
  y += 10;

  // ── Certification paragraphs — all four are typed by the exporter ───
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);

  typeParagraph(ed.para1, 6);  // truck operator certification
  typeParagraph(ed.para2, 6);  // country of origin
  typeParagraph(ed.para3, 6);  // shipped in conformity
  typeParagraph(ed.para4, 6);  // goods strictly as per proforma

  // The signature keeps its own clear gap below whichever paragraph ran last
  y += 10;

  // ── Signature ─────────────────────────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  const forText = 'FOR, ';
  doc.text(forText, M, y);
  doc.setFont(FONT, 'bold');
  doc.text(sv(company.exporter_name, '').toUpperCase(), M + doc.getTextWidth(forText), y);
  y += 15;

  doc.setFont(FONT, 'normal');
  doc.text('AUTHORISED SIGNATORY.', M, y);

  // ── Page footer ───────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);

    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);

    doc.text(footerDate, M, ph - 5);
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });

    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`BeneficiaryCertificate_${sv(document.invoice_no, document.id)}.pdf`);
}

// ══════════════════════════════════════════════════════════════════════
// Beneficiary Certificate Content Type II (no border)
// ══════════════════════════════════════════════════════════════════════
export function generateBeneficiaryCertificateTypeIIPDF(document, company, authority, returnBlob = false) {
  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, 'beneficiaryCertificateTypeII');
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2;

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  let y = M + 5;

  // ── Company Letterhead ──────────────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont(FONT, 'bold');
  doc.text(sv(company.exporter_name, '').toUpperCase(), M, y);
  y += 6;

  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');
  const addrLines = doc.splitTextToSize(sv(company.exporter_address), cw * 0.6);
  doc.text(addrLines, M, y);
  y += addrLines.length * 4 + 2;

  if (company.email) {
    doc.text(`MAIL:${sv(company.email)},`, M, y);
    y += 4;
  }
  if (company.gstin) {
    doc.text(`GSTIN: ${sv(company.gstin)}`, M, y);
    y += 4;
  }
  doc.text(`IEC NO: ${sv(company.iec)}`, M, y);
  y += 6;

  // Horizontal line
  doc.line(M, y, pw - M, y);
  y += 8;

  // ── Date (right aligned, current date) ──────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  doc.text(ed.date_line, pw - M, y, { align: 'right' });
  y += 6;

  // ── Title: BENEFICIARY CERTIFICATE ──────────────────────────────────
  doc.setFont(FONT, 'bold');
  doc.setFontSize(11);
  const title1 = 'BENEFICIARY CERTIFICATE';
  drawSpacedTitle(doc, title1, { cx: pw / 2, y, charSpace: 1.5 });
  y += 8;

  // ── Structured Ref Block (bordered rectangle) ──────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);

  const refBoxX = M + 8;
  const refBoxW = cw - 8;
  const refIndent = refBoxX + 3;
  const refW = refBoxW - 8;
  const refBoxStartY = y - 3;

  // Ref line 1: L/C details
  doc.setFont(FONT, 'bold');
  doc.text('Ref :', refIndent, y);
  doc.setFont(FONT, 'normal');
  const refLine1 = ed.ref_line1;
  const refLine1Lines = doc.splitTextToSize(refLine1, refW);
  doc.text(refLine1Lines, refIndent + 12, y);
  y += refLine1Lines.length * 4;

  // Ref line 2: Invoice
  const refLine2 = ed.ref_line2;
  doc.text(refLine2, refIndent, y);
  y += 4;

  // Ref line 3: Consignee/Buyer
  const refLine3 = ed.ref_line3;
  const refLine3Lines = doc.splitTextToSize(refLine3, refW);
  doc.text(refLine3Lines, refIndent, y);
  y += refLine3Lines.length * 4;

  // Ref line 4: Proforma + TIN + VAT/BIN + IRC + ISSUING BANK BIN + HS
  const refLine4 = ed.ref_line4;
  const refLine4Lines = doc.splitTextToSize(refLine4, refW);
  doc.text(refLine4Lines, refIndent, y);
  y += refLine4Lines.length * 4 + 2;

  // Draw bordered rectangle around ref block
  doc.rect(refBoxX, refBoxStartY, refBoxW, y - refBoxStartY);
  y += 8;

  // ── Title: TO WHOM IT MAY CONCERN ───────────────────────────────────
  doc.setFont(FONT, 'bold');
  doc.setFontSize(11);
  const title2 = 'TO WHOM IT MAY CONCERN';
  drawSpacedTitle(doc, title2, { cx: pw / 2, y, charSpace: 2 });
  y += 8;

  // ── Certification Paragraphs ────────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);

  // Para 1: Truck operator certification
  const para1 = ed.para1;
  const para1Lines = doc.splitTextToSize(para1, cw - 6);
  doc.text(para1Lines, M + 3, y);
  y += para1Lines.length * 4 + 4;

  // Para 2: Country of origin
  const para2 = ed.para2;
  const para2Lines = doc.splitTextToSize(para2, cw - 6);
  doc.text(para2Lines, M + 3, y);
  y += para2Lines.length * 4 + 4;

  // Para 3: Shipped in conformity
  const para3 = ed.para3;
  const para3Lines = doc.splitTextToSize(para3, cw - 6);
  doc.text(para3Lines, M + 3, y);
  y += para3Lines.length * 4 + 4;

  // Para 4: Goods strictly as per proforma
  const para4 = ed.para4;
  const para4Lines = doc.splitTextToSize(para4, cw - 6);
  doc.text(para4Lines, M + 3, y);
  y += para4Lines.length * 4 + 4;

  // Para 5: Non-negotiable shipping documents sent
  const para5 = ed.para5;
  const para5Lines = doc.splitTextToSize(para5, cw - 6);
  doc.text(para5Lines, M + 3, y);
  y += para5Lines.length * 4 + 4;

  // Para 6: Pre-inspection
  const para6 = ed.para6;
  const para6Lines = doc.splitTextToSize(para6, cw - 6);
  doc.text(para6Lines, M + 3, y);
  y += para6Lines.length * 4 + 12;

  // ── Signature ─────────────────────────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  const forText = 'FOR, ';
  doc.text(forText, M, y);
  doc.setFont(FONT, 'bold');
  doc.text(sv(company.exporter_name, '').toUpperCase(), M + doc.getTextWidth(forText), y);
  y += 15;

  doc.setFont(FONT, 'bold');
  doc.text('AUTHORISED SIGNATORY.', M, y);

  // ── Page footer ───────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);

    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);

    doc.text(footerDate, M, ph - 5);
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });

    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`BeneficiaryCertificateTypeII_${sv(document.invoice_no, document.id)}.pdf`);
}

// ══════════════════════════════════════════════════════════════════════
// Phytosanitary Certificate (no border, large top margin for pre-print)
// ══════════════════════════════════════════════════════════════════════
export function generatePhytosanitaryCertificatePDF(document, company, authority, returnBlob = false) {
  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, 'phytosanitaryCertificate');
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2;

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  // ── Large blank space at top (pre-printed form area) ────────────────
  let y = 60;

  // ── Date (right-aligned, DD.MM.YYYY) ────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(10);
  doc.text(ed.date_line, pw - M, y, { align: 'right' });
  y += 16;

  // ── Title: PHYTOSANITARY CERTIFICATE ────────────────────────────────
  doc.setFont(FONT, 'bold');
  doc.setFontSize(12);
  const title = 'PHYTOSANITARY CERTIFICATE';
  doc.text(title, M, y, { charSpace: 1.5 });
  const titleWidth = doc.getTextWidth(title) + (title.length - 1) * 1.5;
  doc.line(M, y + 1, M + titleWidth, y + 1);
  y += 12;

  // ── Form Data (indented to center-right) ────────────────────────────
  const dataX = pw * 0.38;
  const dataW = pw - dataX - M;

  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);

  // Exporter: Company name + address + email + GSTIN
  const companyLines = doc.splitTextToSize(ed.exporter_block, dataW);
  doc.text(companyLines, dataX, y);
  y += companyLines.length * 4 + 4;

  // Consignee: Buyer name + address
  const buyerLines = doc.splitTextToSize(ed.buyer_block, dataW);
  doc.text(buyerLines, dataX, y);
  y += buyerLines.length * 4 + 4;

  // L/C details
  const lcLines = doc.splitTextToSize(ed.lc_text, dataW);
  doc.text(lcLines, dataX, y);
  y += lcLines.length * 4 + 4;

  // Proforma Invoice
  doc.text(ed.proforma_line, dataX, y);
  y += 6;

  // Invoice No & Date
  doc.text(ed.invoice_line, dataX, y);
  y += 6;

  // Description of Goods
  const commodityLines = doc.splitTextToSize(ed.goods_line, dataW);
  doc.text(commodityLines, dataX, y);
  y += commodityLines.length * 4 + 4;

  // HS Code
  doc.setFont(FONT, 'bold');
  doc.text(ed.hs_code, dataX, y);
  doc.setFont(FONT, 'normal');
  y += 6;

  // Net Weight / Packing
  doc.text(ed.weight_packing, dataX, y);
  y += 8;

  // Quality & Packing standards
  doc.text(ed.quality_line, dataX, y);
  y += 5;
  doc.text(ed.packing_line, dataX, y);
  y += 16;

  // ── Bottom Declaration ──────────────────────────────────────────────
  doc.setFontSize(8);
  const declaration = ed.declaration;
  const declLines = doc.splitTextToSize(declaration, cw);
  doc.text(declLines, M, y);

  // ── Page footer ───────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);

    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);

    doc.text(footerDate, M, ph - 5);
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });

    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`PhytosanitaryCertificate_${sv(document.invoice_no, document.id)}.pdf`);
}

// ══════════════════════════════════════════════════════════════════════
// Radioactivity Test Certificate (no border, large top margin for pre-print)
// ══════════════════════════════════════════════════════════════════════
export function generateRadioactivityTestCertificatePDF(document, company, authority, returnBlob = false) {
  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, 'radioactivityTestCertificate');
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2;

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  // ── Large blank space at top (pre-printed form area) ────────────────
  let y = 60;

  // ── Date (right-aligned, DD.MM.YYYY) ────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(10);
  doc.text(ed.date_line, pw - M, y, { align: 'right' });
  y += 16;

  // ── Title: RADIOACTIVITY TEST CERTIFICATE ───────────────────────────
  doc.setFont(FONT, 'bold');
  doc.setFontSize(12);
  const title = 'RADIOACTIVITY TEST CERTIFICATE';
  doc.text(title, M, y, { charSpace: 1.5 });
  const titleWidth = doc.getTextWidth(title) + (title.length - 1) * 1.5;
  doc.line(M, y + 1, M + titleWidth, y + 1);
  y += 12;

  // ── Form Data (indented to center-right) ────────────────────────────
  const dataX = pw * 0.38;
  const dataW = pw - dataX - M;

  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);

  // Exporter: Company name + address + email + GSTIN
  const companyLines = doc.splitTextToSize(ed.exporter_block, dataW);
  doc.text(companyLines, dataX, y);
  y += companyLines.length * 4 + 4;

  // Consignee: Buyer name + address
  const buyerLines = doc.splitTextToSize(ed.buyer_block, dataW);
  doc.text(buyerLines, dataX, y);
  y += buyerLines.length * 4 + 4;

  // L/C details
  const lcLines = doc.splitTextToSize(ed.lc_text, dataW);
  doc.text(lcLines, dataX, y);
  y += lcLines.length * 4 + 4;

  // Proforma Invoice
  doc.text(ed.proforma_line, dataX, y);
  y += 6;

  // Invoice No & Date
  doc.text(ed.invoice_line, dataX, y);
  y += 6;

  // Description of Goods
  const commodityLines = doc.splitTextToSize(ed.goods_line, dataW);
  doc.text(commodityLines, dataX, y);
  y += commodityLines.length * 4 + 4;

  // HS Code
  doc.setFont(FONT, 'bold');
  doc.text(ed.hs_code, dataX, y);
  doc.setFont(FONT, 'normal');
  y += 6;

  // Net Weight / Packing
  doc.text(ed.weight_packing, dataX, y);
  y += 8;

  // Quality & Packing standards
  doc.text(ed.quality_line, dataX, y);
  y += 5;
  doc.text(ed.packing_line, dataX, y);
  y += 16;

  // ── Bottom Declaration (Radioactivity) ──────────────────────────────
  doc.setFontSize(8);
  const declaration = ed.declaration;
  const declLines = doc.splitTextToSize(declaration, cw);
  doc.text(declLines, M, y);

  // ── Page footer ───────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);

    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);

    doc.text(footerDate, M, ph - 5);
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });

    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`RadioactivityTestCertificate_${sv(document.invoice_no, document.id)}.pdf`);
}

// ══════════════════════════════════════════════════════════════════════
// Health Certificate (no border, large top margin for pre-print)
// ══════════════════════════════════════════════════════════════════════
export function generateHealthCertificatePDF(document, company, authority, returnBlob = false) {
  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, 'healthCertificate');
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2;

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  // ── Large blank space at top (pre-printed form area) ────────────────
  let y = 60;

  // ── Date (right-aligned, DD.MM.YYYY) ────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(10);
  doc.text(ed.date_line, pw - M, y, { align: 'right' });
  y += 16;

  // ── Title: HEALTH CERTIFICATE ───────────────────────────────────────
  doc.setFont(FONT, 'bold');
  doc.setFontSize(12);
  const title = 'HEALTH CERTIFICATE';
  doc.text(title, M, y, { charSpace: 1.5 });
  const titleWidth = doc.getTextWidth(title) + (title.length - 1) * 1.5;
  doc.line(M, y + 1, M + titleWidth, y + 1);
  y += 12;

  // ── Form Data (indented to center-right) ────────────────────────────
  const dataX = pw * 0.38;
  const dataW = pw - dataX - M;

  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);

  // Exporter: Company name + address + email + GSTIN
  const companyLines = doc.splitTextToSize(ed.exporter_block, dataW);
  doc.text(companyLines, dataX, y);
  y += companyLines.length * 4 + 4;

  // Consignee: Buyer name + address
  const buyerLines = doc.splitTextToSize(ed.buyer_block, dataW);
  doc.text(buyerLines, dataX, y);
  y += buyerLines.length * 4 + 4;

  // L/C details
  const lcLines = doc.splitTextToSize(ed.lc_text, dataW);
  doc.text(lcLines, dataX, y);
  y += lcLines.length * 4 + 4;

  // Proforma Invoice
  doc.text(ed.proforma_line, dataX, y);
  y += 6;

  // Invoice No & Date
  doc.text(ed.invoice_line, dataX, y);
  y += 6;

  // Description of Goods
  const commodityLines = doc.splitTextToSize(ed.goods_line, dataW);
  doc.text(commodityLines, dataX, y);
  y += commodityLines.length * 4 + 4;

  // HS Code
  doc.setFont(FONT, 'bold');
  doc.text(ed.hs_code, dataX, y);
  doc.setFont(FONT, 'normal');
  y += 6;

  // Net Weight / Packing
  doc.text(ed.weight_packing, dataX, y);
  y += 8;

  // Quality & Packing standards
  doc.text(ed.quality_line, dataX, y);
  y += 5;
  doc.text(ed.packing_line, dataX, y);
  y += 16;

  // ── Bottom Declaration (Health) ─────────────────────────────────────
  doc.setFontSize(8);
  const declaration = ed.declaration;
  const declLines = doc.splitTextToSize(declaration, cw);
  doc.text(declLines, M, y);

  // ── Page footer ───────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);

    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);

    doc.text(footerDate, M, ph - 5);
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });

    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`HealthCertificate_${sv(document.invoice_no, document.id)}.pdf`);
}

// ══════════════════════════════════════════════════════════════════════
// Letter to Bank for Collection / Negotiation / Purchase of Documents
// ══════════════════════════════════════════════════════════════════════
export function generateLetterToBankPDF(document, company, authority, returnBlob = false) {
  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, 'letterToBank');
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2;

  doc.setLineWidth(0.3);
  doc.setDrawColor(0);

  const p = 2; // cell padding

  // Column positions
  const L = M;
  const MID = M + 90;
  const R = pw - M;
  const IEC_X = M + 143;
  const HALF_L = M + 45;

  let y = M;

  // ── Title ───────────────────────────────────────────────────────────
  doc.setFont(FONT, 'bold');
  doc.setFontSize(11);
  doc.text('LETTER TO BANK FOR COLLECTION/ NEGOTIATION/', pw / 2, y + 6, { align: 'center' });
  doc.text('PURCHASE OF DOCUMENTS', pw / 2, y + 12, { align: 'center' });
  y += 16;

  // ══════════════════════════════════════════════════════════════════════
  // TOP INFO TABLE
  // ══════════════════════════════════════════════════════════════════════

  // Row 1: Drawer (Exporter) | Invoice No & Dt | Exporter Reference
  const r1y = y;
  const r1h = 42;
  const invH = 22;

  // Drawer cell (tall, spans full left)
  doc.rect(L, r1y, MID - L, r1h);
  doc.setFont(FONT, 'bolditalic');
  doc.setFontSize(8);
  doc.text('Drawer (Exporter) :', L + p, r1y + 4);
  doc.setFont(FONT, 'normal');
  const drawerText = ed.drawer_block;
  const drawerLines = doc.splitTextToSize(drawerText, MID - L - p * 2);
  doc.text(drawerLines, L + p, r1y + 8);

  // Invoice cell (top-right sub-cell)
  doc.rect(MID, r1y, IEC_X - MID, invH);
  doc.setFont(FONT, 'bolditalic');
  doc.text('Invoice No. & Dt. :', MID + p, r1y + 4);
  doc.setFont(FONT, 'normal');
  doc.text(ed.invoice_ref_line1, MID + p, r1y + 10);
  doc.text(ed.invoice_ref_line2, MID + p, r1y + 14);

  // IEC cell (top-right sub-cell)
  doc.rect(IEC_X, r1y, R - IEC_X, invH);
  doc.setFont(FONT, 'bolditalic');
  doc.text('Exporter', IEC_X + p, r1y + 4);
  doc.text('Reference :', IEC_X + p, r1y + 8);
  doc.setFont(FONT, 'normal');
  doc.text(`IEC :`, IEC_X + p, r1y + 13);
  doc.text(ed.iec_value, IEC_X + p, r1y + 17);

  // Buyer's order cell (below Invoice+IEC, spans both)
  const buyOrdY = r1y + invH;
  const buyOrdH = r1h - invH;
  doc.rect(MID, buyOrdY, R - MID, buyOrdH);
  doc.setFont(FONT, 'bolditalic');
  doc.text("Buyer's order no. & Dt. :", MID + p, buyOrdY + 4);
  doc.setFont(FONT, 'normal');
  const buyOrdText = ed.buyer_order;
  const buyOrdLines = doc.splitTextToSize(buyOrdText, R - MID - p * 2);
  doc.text(buyOrdLines, MID + p, buyOrdY + 9);

  // Row 2: Drawee (Consignee) | Buyer (if other)
  const r2y = r1y + r1h;
  const r2h = 18;
  doc.rect(L, r2y, MID - L, r2h);
  doc.setFont(FONT, 'bolditalic');
  doc.text('Drawee (Consignee) :', L + p, r2y + 4);
  doc.setFont(FONT, 'normal');
  const consText = ed.consignee_block;
  const consLines = doc.splitTextToSize(consText, MID - L - p * 2);
  doc.text(consLines, L + p, r2y + 8);

  doc.rect(MID, r2y, R - MID, r2h);
  doc.setFont(FONT, 'bolditalic');
  doc.text('Buyer (If other than consignee) :', MID + p, r2y + 4);

  // Row 3: Drawee Bank | For Bank Use (spans down)
  const r3y = r2y + r2h;
  const r3h = 20;
  doc.rect(L, r3y, MID - L, r3h);
  doc.setFont(FONT, 'bolditalic');
  doc.text('Drawee Bank :', L + p, r3y + 4);
  doc.setFont(FONT, 'normal');
  const bankText = ed.drawee_bank;
  const bankLines = doc.splitTextToSize(bankText, MID - L - p * 2);
  doc.text(bankLines, L + p, r3y + 8);

  // Row 4: Country | Mode (within left column)
  const r4y = r3y + r3h;
  const r4h = 12;
  doc.rect(L, r4y, HALF_L - L, r4h);
  doc.setFont(FONT, 'bolditalic');
  doc.text('Country of Origin :', L + p, r4y + 4);
  doc.setFont(FONT, 'normal');
  doc.text(ed.country_of_origin, L + p, r4y + 9);

  doc.rect(HALF_L, r4y, MID - HALF_L, r4h);
  doc.setFont(FONT, 'bolditalic');
  doc.text('Mode of Shipment :', HALF_L + p, r4y + 4);
  doc.setFont(FONT, 'normal');
  doc.text(ed.mode_of_shipment, HALF_L + p, r4y + 9);

  // Row 5: Shipment From | Shipment To (within left column)
  const r5y = r4y + r4h;
  const r5h = 15;
  doc.rect(L, r5y, HALF_L - L, r5h);
  doc.setFont(FONT, 'bolditalic');
  doc.setFontSize(7);
  doc.text('Shipment From/ Port of', L + p, r5y + 4);
  doc.text('Loading :', L + p, r5y + 7);
  doc.setFont(FONT, 'normal');
  doc.setFontSize(8);
  doc.text(ed.shipment_from, L + p, r5y + 12);

  doc.rect(HALF_L, r5y, MID - HALF_L, r5h);
  doc.setFont(FONT, 'bolditalic');
  doc.setFontSize(7);
  doc.text('Shipment To/ Port of', HALF_L + p, r5y + 4);
  doc.text('Discharge :', HALF_L + p, r5y + 7);
  doc.setFont(FONT, 'normal');
  doc.setFontSize(8);
  const stLines = doc.splitTextToSize(ed.shipment_to, MID - HALF_L - p * 2);
  doc.text(stLines, HALF_L + p, r5y + 12);

  // For Bank Use cell (right side, spans rows 3-5)
  const fbY = r3y;
  const fbH = (r5y + r5h) - r3y;
  doc.rect(MID, fbY, R - MID, fbH);
  doc.setFont(FONT, 'bolditalic');
  doc.setFontSize(8);
  doc.text('For Bank Use :', MID + p, fbY + 4);

  y = r5y + r5h;

  // ══════════════════════════════════════════════════════════════════════
  // DOCUMENTS + INSTRUCTIONS TABLE
  // ══════════════════════════════════════════════════════════════════════

  const docCol = L;
  const numCol = L + 73;
  const instrCol = L + 95;
  const tickCol = R - 12;

  // Gray header
  doc.setFillColor(220, 220, 220);
  doc.rect(docCol, y, instrCol - docCol, 12, 'FD');
  doc.rect(instrCol, y, R - instrCol, 12, 'FD');
  doc.setFont(FONT, 'bold');
  doc.setFontSize(8);
  doc.text('Please receive the following documents for', docCol + p, y + 5);
  doc.text('disposal as per instructions :', docCol + p, y + 10);
  doc.text('Instruction (Please', instrCol + p, y + 5);
  doc.text('tick as required)', instrCol + p, y + 10);
  y += 12;

  // Sub-header: DOCUMENTS | NUMBER
  doc.rect(docCol, y, numCol - docCol, 7);
  doc.rect(numCol, y, instrCol - numCol, 7);
  doc.setFont(FONT, 'bold');
  doc.text('DOCUMENTS', docCol + 15, y + 5);
  doc.text('NUMBER', numCol + 2, y + 5);
  y += 7;

  // Row data: paired documents (left) and instructions (right)
  const rows = [
    { doc: ed.row_invoice, instr: 'i. Collect', h: 14 },
    { doc: ed.row_packing, instr: 'ii. Purchase / Discount', h: 14 },
    { doc: ed.row_tr, instr: 'iii. Despatch documents by registered air mail/\ncourier', h: 14 },
    { doc: ed.row_sb, instr: 'iv. Release documents against payment/\nacceptance', h: 14 },
    { doc: 'v. Certificate of Origin', instr: 'vi. Cable advice of non-payment/ non-acceptance', h: 12 },
    { doc: 'vi. Inspection Certificate', instr: 'vii. In case of non-payment/ non-acceptance,\nprotest/ do not protest', h: 14 },
    { doc: 'vii. Insurance Policy (if any)', instr: ed.instr_lc, h: 28 },
    { doc: 'viii. Insurance Certificate for', instr: ed.instr_credit, h: 26 },
    { doc: ed.row_boe, instr: 'x. Advice payment by Telex/ Cable/ FAX / SWIFT', h: 14 },
    { doc: ed.row_lc, note: '[ORIGINAL]', instr: 'xi. Deduct charges / collect charges from us\napplicable in India/ Re-imb, bank charges', h: 14 },
    { doc: 'xi. EDF / GR. / SDF', instr: 'xii. Do not waive charges / Interest recoverable\nfrom drawee', h: 14 },
    { doc: 'xii. Non-Negotiable Shipping Documents', instr: 'xiii. Collect overdue interest @ p.a. from L/C\nopening', h: 14 },
    { doc: "xiii. Beneficiary's Certificate", instr: '', h: 12 },
  ];

  doc.setFont(FONT, 'normal');
  doc.setFontSize(8);

  for (const row of rows) {
    // Page break check
    if (y + row.h > ph - 20) {
      doc.addPage();
      y = M;
    }

    // Document cell
    doc.rect(docCol, y, numCol - docCol, row.h);
    const docLines = doc.splitTextToSize(row.doc, numCol - docCol - p * 2);
    doc.text(docLines, docCol + p, y + 5);

    // NUMBER box cell
    doc.rect(numCol, y, instrCol - numCol, row.h);
    doc.rect(numCol + 6, y + row.h / 2 - 3, 8, 6); // small box
    if (row.note) {
      doc.text(row.note, numCol + 2, y + row.h / 2 + 8);
    }

    // Instruction cell
    doc.rect(instrCol, y, tickCol - instrCol, row.h);
    if (row.instr) {
      const instrLines = doc.splitTextToSize(row.instr, tickCol - instrCol - p * 2);
      doc.text(instrLines, instrCol + p, y + 5);
    }

    // Checkbox cell
    doc.rect(tickCol, y, R - tickCol, row.h);
    if (row.instr) {
      doc.rect(tickCol + 3, y + row.h / 2 - 3, 6, 6); // checkbox
    }

    y += row.h;
  }

  // ══════════════════════════════════════════════════════════════════════
  // BOTTOM: Declarations + Signature
  // ══════════════════════════════════════════════════════════════════════

  if (y + 25 > ph - 15) {
    doc.addPage();
    y = M;
  }

  const declH = 25;
  // Declarations cell (left)
  doc.rect(docCol, y, instrCol - docCol, declH);
  doc.setFont(FONT, 'bolditalic');
  doc.text('Declarations', docCol + p, y + 5);
  doc.setFont(FONT, 'normal');
  const declText = "I/ We declare that the particulars given herein are true\nand correct and that I/ We accept the bank's conditions\nprinted overleaf.";
  doc.text(declText, docCol + p, y + 10);

  // Signature cell (right)
  doc.rect(instrCol, y, R - instrCol, declH);
  doc.setFont(FONT, 'normal');
  const forTxt = 'For, ';
  doc.text(forTxt, instrCol + p, y + 6);
  doc.setFont(FONT, 'bold');
  const compName = sv(company.exporter_name, '').toUpperCase();
  const compNameLines = doc.splitTextToSize(compName, R - instrCol - p * 2 - doc.getTextWidth(forTxt));
  doc.text(compNameLines, instrCol + p + doc.getTextWidth(forTxt), y + 6);
  doc.text(ed.signatory, R - p, y + declH - 4, { align: 'right' });

  // ── Page footer ───────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text(footerDate, M, ph - 5);
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });
    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`LetterToBank_${sv(document.invoice_no, document.id)}.pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// TERMS & CONDITIONS PDF
// ═══════════════════════════════════════════════════════════════════════════════

export function generateTermsConditionsPDF(document, company, authority, returnBlob = false) {
  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, 'termsConditions');
  const FONT = 'times'; // T&C uses serif font to match bank document format
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  doc.setLineWidth(LW);

  const leftM = 20;
  const rightM = 20;
  const contentW = pw - leftM - rightM;
  const numW = 12;       // width for the number prefix "1. "
  const subNumW = 18;    // indent for sub-items "   a. "
  const lineH = 5;       // line height for body text

  let y = 30;

  // ── Title ──
  doc.setFont(FONT, 'bold');
  doc.setFontSize(14);
  doc.text('TERMS & CONDITIONS', pw / 2, y, { align: 'center' });
  y += 14;

  // ── Helper: add wrapped justified text block ──
  const addParagraph = (text, indentX, maxW) => {
    doc.setFont(FONT, 'normal');
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(text, maxW);
    for (let i = 0; i < lines.length; i++) {
      if (y > ph - 30) {
        doc.addPage();
        y = 20;
      }
      doc.text(lines[i], indentX, y, { maxWidth: maxW, align: 'justify' });
      y += lineH;
    }
  };

  // ── Terms Data ──
  const terms = [ed.term1, ed.term2, ed.term3, ed.term4, ed.term5, ed.term6];

  // Items 1–6
  doc.setFontSize(10);
  for (let i = 0; i < terms.length; i++) {
    if (y > ph - 30) { doc.addPage(); y = 20; }
    doc.setFont(FONT, 'bold');
    doc.text(`${i + 1}.`, leftM, y);
    addParagraph(terms[i], leftM + numW, contentW - numW);
    y += 2;
  }

  // Item 7 with sub-items
  if (y > ph - 30) { doc.addPage(); y = 20; }
  doc.setFont(FONT, 'bold');
  doc.setFontSize(10);
  doc.text('7.', leftM, y);
  doc.setFont(FONT, 'normal');
  doc.text(ed.term7_intro, leftM + numW, y);
  y += lineH + 3;

  const subItems = [ed.term7a, ed.term7b, ed.term7c];

  const subLabels = ['a.', 'b.', 'c.'];
  for (let i = 0; i < subItems.length; i++) {
    if (y > ph - 30) { doc.addPage(); y = 20; }
    doc.setFont(FONT, 'normal');
    doc.setFontSize(10);
    doc.text(subLabels[i], leftM + numW, y);
    addParagraph(subItems[i], leftM + subNumW, contentW - subNumW);
    y += 2;
  }

  // Item 8
  if (y > ph - 30) { doc.addPage(); y = 20; }
  doc.setFont(FONT, 'bold');
  doc.setFontSize(10);
  doc.text('8.', leftM, y);
  addParagraph(ed.term8, leftM + numW, contentW - numW);

  // ── Signature Section ──
  y += 12;
  if (y > ph - 50) { doc.addPage(); y = 20; }

  // Date & Place on left
  doc.setFont(FONT, 'bold');
  doc.setFontSize(11);
  doc.text('Date :', leftM, y);

  // "For, COMPANY NAME" on right side
  doc.setFont(FONT, 'normal');
  const compName = sv(company.exporter_name, '').toUpperCase();
  doc.text(`For,`, pw / 2 + 10, y);
  doc.setFont(FONT, 'bold');
  doc.text(compName, pw / 2 + 20, y);

  y += 14;
  doc.setFont(FONT, 'bold');
  doc.text('Place :', leftM, y);

  // Signing authority type on right
  const authType = ed.signatory;
  doc.setFont(FONT, 'bold');
  doc.text(authType, pw - rightM, y, { align: 'right' });

  // ── Footer ──
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text(footerDate, M, ph - 5);
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });
    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`TermsConditions_${sv(document.invoice_no, document.id)}.pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// COUNTRY OF ORIGIN CERTIFICATE PDF
// ═══════════════════════════════════════════════════════════════════════════════

export function generateCountryOfOriginCertificatePDF(document, company, authority, returnBlob = false) {
  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, 'countryOfOriginCert');
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2;

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  const compName = sv(company.exporter_name, '').toUpperCase();
  const today = new Date();

  let y = M + 5;

  // ── Company Name (bold, large) ────────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont(FONT, 'bold');
  doc.text(compName, M, y);
  y += 6;

  // ── Company Address + Email (wrapped block) ───────────────────────────
  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');
  const addrEmailText = [
    sv(company.exporter_address),
    company.email ? `E MAIL:${sv(company.email)},` : ''
  ].filter(Boolean).join(' ');
  const addrLines = doc.splitTextToSize(addrEmailText, cw * 0.45);
  doc.text(addrLines, M, y);
  y += addrLines.length * 4 + 2;

  // ── GSTIN ─────────────────────────────────────────────────────────────
  if (company.gstin) {
    doc.text(`GSTIN: ${sv(company.gstin)}`, M, y);
    y += 4;
  }

  // ── IEC ───────────────────────────────────────────────────────────────
  doc.text(`IEC NO: ${sv(company.iec)}`, M, y);
  y += 6;

  // ── Black horizontal line closing the letterhead ─────────────────────
  doc.setLineWidth(0.5);
  doc.line(M, y, pw - M, y);
  doc.setLineWidth(LW);
  y += 8;

  // ── Date (right-aligned, italic) ──────────────────────────────────────
  doc.setFont(FONT, 'italic');
  doc.setFontSize(10);
  doc.text(ed.date_line, pw - M, y, { align: 'right' });
  y += 14;

  // ── Title: TO WHOMSOEVER IT MAY CONCERN (centered, bold, underlined, spaced) ──
  doc.setFont(FONT, 'bold');
  doc.setFontSize(12);
  drawSpacedTitle(doc, 'TO WHOMSOEVER IT MAY CONCERN', { cx: pw / 2, y, charSpace: 1.5 });
  y += 16;

  // ── Body Paragraph ────────────────────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(10);

  const bodyText = ed.body_text;

  // Flush with the letterhead and the rule above it, both edges justified
  // the way the certificate is typed; jsPDF leaves the last line ragged.
  doc.setLineHeightFactor(1.4);
  doc.text(bodyText, M, y, { align: 'justify', maxWidth: cw });
  const bodyLines = doc.splitTextToSize(bodyText, cw);
  const bodyHeight = bodyLines.length * doc.getLineHeightFactor() * doc.getFontSize() * MM_PER_PT;
  doc.setLineHeightFactor(1.15);
  y += bodyHeight + 30;

  // ── Check if signature fits, otherwise add page ───────────────────────
  if (y > ph - 40) {
    doc.addPage();
    y = M + 10;
  }

  // ── Signature Block (left-aligned) ────────────────────────────────────
  doc.setFontSize(10);
  doc.setFont(FONT, 'normal');
  const forText = 'FOR, ';
  doc.text(forText, M, y);
  doc.setFont(FONT, 'bold');
  doc.text(compName, M + doc.getTextWidth(forText), y);
  y += 20;

  doc.setFont(FONT, 'bold');
  doc.text('AUTHORISED SIGNATORY.', M, y);

  // ── Page footer ───────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text(footerDate, M, ph - 5);
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });
    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`CountryOfOriginCertificate_${sv(document.invoice_no, document.id)}.pdf`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// SHIPMENT ADVICE TO THE BUYER PDF
// ═══════════════════════════════════════════════════════════════════════════════

export function generateShipmentAdviceBuyerPDF(document, company, authority, returnBlob = false) {
  // Resolved print data: saved edits win, otherwise invoice-derived defaults
  const ed = resolvePdfData(document, company, authority, 'shipmentAdviceBuyer');
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2;

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  let y = M + 5;

  // ── Company Letterhead ────────────────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont(FONT, 'bold');
  doc.text(sv(company.exporter_name, '').toUpperCase(), M, y);
  y += 6;

  doc.setFontSize(9);
  doc.setFont(FONT, 'normal');
  const addrLines = doc.splitTextToSize(sv(company.exporter_address), cw * 0.6);
  doc.text(addrLines, M, y);
  y += addrLines.length * 4 + 2;

  if (company.email) {
    doc.text(`MAIL:${sv(company.email)},`, M, y);
    y += 4;
  }
  if (company.gstin) {
    doc.text(`GSTIN: ${sv(company.gstin)}`, M, y);
    y += 4;
  }
  doc.text(`IEC NO: ${sv(company.iec)}`, M, y);
  y += 6;

  // Horizontal line
  doc.line(M, y, pw - M, y);
  y += 8;

  // ── Date (right aligned) ──────────────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  doc.text(ed.date_line, pw - M, y, { align: 'right' });
  y += 6;

  // ── Title ─────────────────────────────────────────────────────────────
  doc.setFont(FONT, 'bold');
  doc.setFontSize(11);

  const title1 = 'BENEFICIARY CERTIFICATE';
  const title1Width = doc.getTextWidth(title1);
  doc.text(title1, pw / 2, y, { align: 'center' });
  doc.line(pw / 2 - title1Width / 2, y + 1, pw / 2 + title1Width / 2, y + 1);
  y += 5;

  const title2 = 'SHIPMENT ADVICE';
  const title2Width = doc.getTextWidth(title2);
  doc.text(title2, pw / 2, y, { align: 'center' });
  doc.line(pw / 2 - title2Width / 2, y + 1, pw / 2 + title2Width / 2, y + 1);
  y += 8;

  // ── TO Section (Buyer) ────────────────────────────────────────────────
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  doc.text('TO,', M, y);
  y += 4;

  const toLines = doc.splitTextToSize(ed.to_block, cw * 0.5);
  doc.text(toLines, M, y);
  y += toLines.length * 4 + 6;

  // ── Salutation ────────────────────────────────────────────────────────
  doc.text('DEAR SIR,', M, y);
  y += 6;

  // ── Ref Section ───────────────────────────────────────────────────────
  const refIndent = M + 10;
  const refFirstX = refIndent + 12;
  doc.setFont(FONT, 'bold');
  doc.text('Ref :', refIndent, y);
  doc.setFont(FONT, 'normal');
  // One hanging paragraph, the way the letter is typed: only the opening line
  // clears the label, while the proforma and cover-note lines hang at the
  // block's left edge with the rest of the L/C wording. A ref line the invoice
  // has no data for is skipped rather than printed as a blank row.
  let refFirst = true;
  [ed.ref_line1, ed.ref_line2, ed.ref_line3].forEach((refLine) => {
    if (!sv(refLine).trim()) return;
    y += drawHangingParagraph(doc, refLine, {
      x: refIndent,
      firstX: refFirst ? refFirstX : refIndent,
      rightX: pw - M,
      y,
    });
    refFirst = false;
  });

  // Underline the entire ref block
  doc.line(refIndent, y, pw - M, y);
  y += 6;

  // ── Body paragraph ────────────────────────────────────────────────────
  doc.setFontSize(9);
  const bodyText = ed.body_text;
  const bodyLines = doc.splitTextToSize(bodyText, cw);
  doc.text(bodyLines, M, y, { align: 'justify', maxWidth: cw });
  y += bodyLines.length * 4 + 4;

  // ── Numbered Detail Rows ──────────────────────────────────────────────
  const labelX = M + 2;
  const colonX = M + 72;
  const valueX = M + 76;
  const valueW = cw - 76;

  doc.text('01. CONSIGNEE/ BUYER/ APPLICANT', labelX, y);
  doc.text(':', colonX, y);
  const buyerVal = ed.buyer_block;
  const buyerDetailLines = doc.splitTextToSize(buyerVal, valueW);
  doc.text(buyerDetailLines, valueX, y);
  y += buyerDetailLines.length * 4 + 2;

  doc.text('02. INVOICE NO. & DATE', labelX, y);
  doc.text(':', colonX, y);
  doc.text(ed.invoice_line, valueX, y);
  y += 6;

  doc.text('03. INVOICE VALUE', labelX, y);
  doc.text(':', colonX, y);
  doc.text(ed.invoice_value, valueX, y);
  y += 6;

  doc.text('04. DESCRIPTION OF GOODS', labelX, y);
  doc.text(':', colonX, y);
  const goodsLines = doc.splitTextToSize(ed.goods_line, valueW);
  doc.text(goodsLines, valueX, y);
  y += goodsLines.length * 4 + 2;

  doc.text('05. NET WEIGHT & GROSS WEIGHT', labelX, y);
  doc.text(':', colonX, y);
  doc.text(ed.weight_line, valueX, y);
  y += 6;

  doc.text('06. DESPATCH DOCUMENT NO. &', labelX, y);
  y += 4;
  doc.text('DATE', labelX + 6, y);
  doc.text(':', colonX, y);
  doc.text(ed.despatch_line, valueX, y);
  y += 6;

  doc.text('07. CARRIER', labelX, y);
  doc.text(':', colonX, y);
  const carrierText = ed.carrier;
  const carrierLines = doc.splitTextToSize(carrierText, valueW);
  doc.text(carrierLines, valueX, y);
  y += carrierLines.length * 4 + 6;

  // ── Closing ───────────────────────────────────────────────────────────
  doc.text(ed.closing_line, M + 6, y);
  y += 10;

  doc.text('THANKING YOU,', M, y);
  y += 6;
  doc.text('YOURS FAITHFULLY,', M, y);
  y += 4;
  doc.setFont(FONT, 'bold');
  doc.text(`FOR, ${sv(company.exporter_name, '').toUpperCase()}`, M, y);
  y += 12;
  doc.setFont(FONT, 'normal');
  doc.text('AUTHORISED SIGNATORY.', M, y);

  // ── Page footer ───────────────────────────────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text(footerDate, M, ph - 5);
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });
    doc.text(sv(company.exporter_name), pw - M, ph - 5, { align: 'right' });
    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`ShipmentAdviceBuyer_${sv(document.invoice_no, document.id)}.pdf`);
}
